"use client"
var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/components/ui/button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

// src/lib/utils.ts
import { clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
var twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          text: [
            // Paragraph
            "paragraph-large",
            "paragraph-medium",
            "paragraph-small",
            "paragraph-xsmall",
            // Overline
            "overline-large",
            "overline-small",
            "paragraph-overline-large",
            "paragraph-overline-small",
            // Label
            "label-large",
            "label-medium",
            "label-small",
            "label-xsmall",
            // Heading
            "heading-h1",
            "heading-h2",
            "heading-h3",
            "heading-h4",
            "heading-h5",
            "heading-h6",
            "heading-h1-mobile",
            "heading-h2-mobile",
            "heading-h3-mobile",
            "heading-h4-mobile",
            "heading-h5-mobile",
            "heading-h6-mobile",
            // Display
            "display-large",
            "display-small"
          ]
        }
      ]
    }
  }
});
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/ui/button.tsx
import { jsx } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex appearance-none items-center justify-center select-none relative whitespace-nowrap align-middle border border-transparent cursor-pointer shrink-0 outline-0 isolate font-medium transition-all duration-200 focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary: "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 focus-visible:ring-primary-500",
        secondary: "border border-neutral-200 text-neutral-800 hover:bg-neutral-50 active:bg-neutral-50 focus-visible:ring-neutral-500",
        ghost: "bg-transparent text-primary-600 hover:bg-neutral-100/10 focus-visible:ring-primary-500",
        plain: "bg-transparent text-neutral-800 focus-visible:ring-neutral-500",
        outline: "bg-transparent border border-primary-200 text-primary-800 hover:bg-primary-200 focus-visible:ring-primary-500",
        dark: "bg-generic-100 text-generic-0 hover:bg-generic-100/90 active:bg-generic-100/80 focus-visible:ring-neutral-500"
      },
      size: {
        "2xs": "h-6 min-w-6 text-xs px-2 gap-1 [&_svg]:w-3.5 [&_svg]:h-3.5",
        xs: "h-8 min-w-8 text-xs px-2.5 gap-1 [&_svg]:w-4 [&_svg]:h-4",
        sm: "h-9 min-w-9 text-sm px-3.5 gap-2 rounded-[4px] [&_svg]:w-4 [&_svg]:h-4",
        md: "h-10 min-w-10 text-sm px-4 gap-2 rounded-[8px] [&_svg]:w-5 [&_svg]:h-5",
        lg: "h-11 min-w-11 text-base px-5 gap-3 [&_svg]:w-5 [&_svg]:h-5",
        xl: "h-12 min-w-12 text-base px-5 gap-2.5 [&_svg]:w-5 [&_svg]:h-5",
        "2xl": "h-16 min-w-16 text-lg px-7 gap-3 [&_svg]:w-6 [&_svg]:h-6",
        icon: "h-10 w-10 p-0 [&_svg]:w-5 [&_svg]:h-5"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "md"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, loading, loadingText, colorPalette: _colorPalette, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        disabled: loading || props.disabled,
        ...props,
        children: loading ? loadingText ?? props.children : props.children
      }
    );
  }
);
Button.displayName = "Button";

// src/components/ui/input.tsx
import * as React2 from "react";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx2 } from "react/jsx-runtime";
var inputVariants = cva2(
  "flex w-full appearance-none bg-transparent text-neutral-900 text-start transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-neutral-400 disabled:cursor-not-allowed disabled:opacity-50 outline-none aria-invalid:border-error-500 aria-invalid:ring-error-500",
  {
    variants: {
      variant: {
        outline: "border border-neutral-200 focus-visible:ring-0 focus-visible:border-neutral-700",
        subtle: "bg-neutral-50 border border-transparent focus-visible:ring-0 focus-visible:border-neutral-700",
        flushed: "border-b border-neutral-200 rounded-none px-0 focus-visible:border-primary-500 focus-visible:shadow-[0_1px_0_0_theme(colors.primary.500)]"
      },
      size: {
        "2xs": "h-7 px-2 text-xs rounded-sm",
        xs: "h-8 px-2 text-xs rounded-sm",
        sm: "h-9 px-2.5 text-sm rounded-sm",
        md: "h-10 px-3 text-sm rounded-sm",
        lg: "h-11 px-4 text-base rounded-sm",
        xl: "h-12 px-[18px] text-base rounded-sm",
        "2xl": "h-16 px-5 text-lg rounded-sm"
      }
    },
    defaultVariants: {
      variant: "outline",
      size: "md"
    }
  }
);
var Input = React2.forwardRef(({ className, variant, size, type, ...props }, ref) => {
  return /* @__PURE__ */ jsx2("input", { type, className: cn(inputVariants({ variant, size }), className), ref, ...props });
});
Input.displayName = "Input";

// src/components/ui/textarea.tsx
import * as React3 from "react";
import { cva as cva3 } from "class-variance-authority";
import { jsx as jsx3 } from "react/jsx-runtime";
var textareaVariants = cva3(
  "flex w-full bg-transparent text-neutral-900 transition-colors placeholder:text-neutral-400 disabled:cursor-not-allowed disabled:opacity-50 outline-none min-h-20 resize-y",
  {
    variants: {
      variant: {
        outline: "border border-neutral-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500",
        subtle: "bg-neutral-50 border border-transparent focus:border-primary-500",
        flushed: "border-b border-neutral-200 rounded-none px-0 focus:border-primary-500"
      },
      size: {
        xs: "px-2 py-1.5 text-xs rounded-md",
        sm: "px-3 py-2 text-sm rounded-md",
        md: "px-3 py-2 text-sm rounded-lg",
        lg: "px-4 py-3 text-base rounded-lg",
        xl: "px-4 py-3 text-base rounded-lg"
      }
    },
    defaultVariants: {
      variant: "outline",
      size: "md"
    }
  }
);
var Textarea = React3.forwardRef(({ className, variant, size, ...props }, ref) => {
  return /* @__PURE__ */ jsx3("textarea", { className: cn(textareaVariants({ variant, size }), className), ref, ...props });
});
Textarea.displayName = "Textarea";

// src/components/ui/radio-group.tsx
import * as React4 from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var RadioGroup = React4.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx4(RadioGroupPrimitive.Root, { ref, className: cn("grid gap-2", className), ...props }));
RadioGroup.displayName = "RadioGroup";
var RadioGroupItem = React4.forwardRef(
  ({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs(
    "label",
    {
      className: cn(
        "flex items-center gap-2 cursor-pointer select-none",
        props.disabled && "cursor-not-allowed opacity-50",
        className
      ),
      children: [
        /* @__PURE__ */ jsx4(
          RadioGroupPrimitive.Item,
          {
            ref,
            className: cn(
              "aspect-square h-4 w-4 shrink-0 rounded-full border border-neutral-300",
              "focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-500",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "data-[state=checked]:border-primary-500"
            ),
            ...props,
            children: /* @__PURE__ */ jsx4(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx4("span", { className: "h-2 w-2 rounded-full bg-primary-500" }) })
          }
        ),
        children
      ]
    }
  )
);
RadioGroupItem.displayName = "RadioGroupItem";

// src/components/ui/select.tsx
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
function Select({ ...props }) {
  return /* @__PURE__ */ jsx5(SelectPrimitive.Root, { "data-slot": "select", ...props });
}
function SelectGroup({ ...props }) {
  return /* @__PURE__ */ jsx5(SelectPrimitive.Group, { "data-slot": "select-group", ...props });
}
function SelectValue({ ...props }) {
  return /* @__PURE__ */ jsx5(SelectPrimitive.Value, { "data-slot": "select-value", ...props });
}
function SelectTrigger({ className, children, ...props }) {
  return /* @__PURE__ */ jsxs2(
    SelectPrimitive.Trigger,
    {
      "data-slot": "select-trigger",
      className: cn(
        // Recipe base: layout
        "flex w-full items-center justify-between min-h-10 px-3 gap-2",
        // Recipe base: appearance
        "rounded-sm bg-transparent text-sm text-neutral-900 cursor-pointer select-none text-start",
        // Recipe variant: outline (stronger border to match Chakra visual)
        "border border-neutral-300",
        "data-[state=open]:border-neutral-400",
        // Recipe base: placeholder
        "data-[placeholder]:text-neutral-400/80",
        // States
        "focus:outline-none focus-visible:ring-1 focus-visible:ring-neutral-900/20",
        "disabled:cursor-not-allowed disabled:opacity-50",
        // ValueText constraints
        "*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:text-left",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx5(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx5(ChevronDownIcon, { "data-slot": "select-icon", className: "size-4 shrink-0 text-neutral-500" }) })
      ]
    }
  );
}
function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}) {
  return /* @__PURE__ */ jsx5(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsx5(
    SelectPrimitive.Content,
    {
      "data-slot": "select-content",
      className: cn(
        // Recipe base
        "relative z-popover flex flex-col bg-white rounded-lg shadow-lg outline-none border border-neutral-100",
        "max-h-96 overflow-y-auto",
        // Recipe size md
        "p-1 text-sm",
        // Animations
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1 min-w-[var(--radix-select-trigger-width)]",
        className
      ),
      position,
      sideOffset: 4,
      ...props,
      children: /* @__PURE__ */ jsx5(SelectPrimitive.Viewport, { className: cn("flex flex-col", position === "popper" && "w-full"), children })
    }
  ) });
}
function SelectLabel({ className, ...props }) {
  return /* @__PURE__ */ jsx5(
    SelectPrimitive.Label,
    {
      "data-slot": "select-label",
      className: cn("px-4 py-1.5 text-base font-medium text-neutral-900 select-none", className),
      ...props
    }
  );
}
function SelectItem({ className, children, ...props }) {
  return /* @__PURE__ */ jsxs2(
    SelectPrimitive.Item,
    {
      "data-slot": "select-item",
      className: cn(
        // Recipe base: layout
        "relative flex w-full items-center justify-between gap-2 flex-1",
        // Recipe size md: spacing
        "py-3 px-4 text-sm",
        // Recipe base: appearance
        "rounded-[4px] bg-white text-neutral-600 cursor-pointer select-none text-start outline-none",
        // Recipe states
        "hover:bg-neutral-50 hover:text-neutral-600",
        "focus:bg-neutral-50 focus:text-neutral-600",
        "data-[state=checked]:bg-primary-100 data-[state=checked]:text-primary-600",
        // Disabled
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx5(SelectPrimitive.ItemText, { className: "flex-1", children }),
        /* @__PURE__ */ jsx5("span", { className: "flex size-3.5 shrink-0 items-center justify-center", children: /* @__PURE__ */ jsx5(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx5(CheckIcon, { className: "size-3.5" }) }) })
      ]
    }
  );
}
function SelectSeparator({ className, ...props }) {
  return /* @__PURE__ */ jsx5(
    SelectPrimitive.Separator,
    {
      "data-slot": "select-separator",
      className: cn("pointer-events-none -mx-1 my-1 h-px bg-neutral-200", className),
      ...props
    }
  );
}

// src/components/ui/checkbox.tsx
import { CheckIcon as CheckIcon2 } from "lucide-react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cva as cva4 } from "class-variance-authority";
import { jsx as jsx6 } from "react/jsx-runtime";
var checkboxVariants = cva4(
  "peer shrink-0 rounded-[4px] border border-neutral-300 bg-white transition-shadow outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
  {
    variants: {
      size: {
        sm: "size-4",
        md: "size-5"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);
var checkIconVariants = cva4("", {
  variants: {
    size: {
      sm: "size-3",
      md: "size-3.5"
    }
  },
  defaultVariants: {
    size: "md"
  }
});
function Checkbox({ className, size, ...props }) {
  return /* @__PURE__ */ jsx6(
    CheckboxPrimitive.Root,
    {
      "data-slot": "checkbox",
      className: cn(checkboxVariants({ size, className })),
      ...props,
      children: /* @__PURE__ */ jsx6(
        CheckboxPrimitive.Indicator,
        {
          "data-slot": "checkbox-indicator",
          className: "grid place-content-center text-current transition-none",
          children: /* @__PURE__ */ jsx6(CheckIcon2, { className: checkIconVariants({ size }) })
        }
      )
    }
  );
}

// src/components/ui/switch.tsx
import * as React5 from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { jsx as jsx7 } from "react/jsx-runtime";
var Switch = React5.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx7(
  SwitchPrimitive.Root,
  {
    ref,
    className: cn(
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary-500 data-[state=unchecked]:bg-neutral-200",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx7(
      SwitchPrimitive.Thumb,
      {
        className: cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = "Switch";

// src/components/ui/slider.tsx
import * as React6 from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
var Slider = React6.forwardRef(
  ({ className, defaultValue, value, trackClassName, rangeClassName, thumbClassName, ...props }, ref) => {
    const thumbCount = (value ?? defaultValue ?? [0]).length;
    return /* @__PURE__ */ jsxs3(
      SliderPrimitive.Root,
      {
        ref,
        className: cn("relative flex w-full touch-none select-none items-center min-h-5", className),
        defaultValue,
        value,
        ...props,
        children: [
          /* @__PURE__ */ jsx8(
            SliderPrimitive.Track,
            {
              className: cn(
                "relative h-2 w-full grow overflow-hidden rounded-full bg-neutral-200/70 shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)]",
                trackClassName
              ),
              children: /* @__PURE__ */ jsx8(SliderPrimitive.Range, { className: cn("absolute h-full bg-primary-500", rangeClassName) })
            }
          ),
          Array.from({ length: thumbCount }, (_, i) => /* @__PURE__ */ jsx8(
            SliderPrimitive.Thumb,
            {
              className: cn(
                "block h-5 w-5 rounded-full border-2 border-primary-500 bg-white shadow transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
                thumbClassName
              )
            },
            i
          ))
        ]
      }
    );
  }
);
Slider.displayName = "Slider";

// src/components/ui/pin-input.tsx
import * as React7 from "react";
import { cva as cva5 } from "class-variance-authority";
import { jsx as jsx9 } from "react/jsx-runtime";
var pinInputFieldVariants = cva5(
  "outline-none appearance-none bg-transparent text-center rounded-lg disabled:opacity-50 disabled:cursor-not-allowed aria-invalid:border-error-500",
  {
    variants: {
      variant: {
        outline: "border border-neutral-200 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary-500 focus-visible:border-primary-500",
        subtle: "bg-neutral-50 border border-transparent focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary-500",
        flushed: "border-b border-neutral-200 rounded-none focus-visible:border-primary-500"
      },
      size: {
        "2xs": "h-7 w-7 text-xs",
        xs: "h-8 w-8 text-xs",
        sm: "h-9 w-9 text-sm",
        md: "h-10 w-10 text-sm",
        lg: "h-11 w-11 text-base",
        xl: "h-12 w-12 text-base",
        "2xl": "h-16 w-16 text-lg"
      }
    },
    defaultVariants: {
      variant: "outline",
      size: "md"
    }
  }
);
var PinInput = React7.forwardRef(
  ({
    length = 4,
    value,
    defaultValue = "",
    onChange,
    onComplete,
    placeholder = "\u25CB",
    disabled,
    type = "numeric",
    mask,
    className,
    variant,
    size,
    attached
  }, ref) => {
    const [values, setValues] = React7.useState(() => {
      const initial = value ?? defaultValue;
      return Array.from({ length }, (_, i) => initial[i] ?? "");
    });
    const inputRefs = React7.useRef([]);
    React7.useEffect(() => {
      if (value !== void 0) {
        setValues(Array.from({ length }, (_, i) => value[i] ?? ""));
      }
    }, [value, length]);
    const updateValues = (newValues) => {
      setValues(newValues);
      const joined = newValues.join("");
      onChange?.(joined);
      if (joined.length === length && newValues.every(Boolean)) {
        onComplete?.(joined);
      }
    };
    const handleInput = (index, inputValue) => {
      const pattern = type === "numeric" ? /^[0-9]$/ : /^[a-zA-Z0-9]$/;
      const char = inputValue.slice(-1);
      if (!char || !pattern.test(char)) return;
      const newValues = [...values];
      newValues[index] = char;
      updateValues(newValues);
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    };
    const handleKeyDown = (index, e) => {
      if (e.key === "Backspace" && !values[index] && index > 0) {
        const newValues = [...values];
        newValues[index - 1] = "";
        updateValues(newValues);
        inputRefs.current[index - 1]?.focus();
      }
      if (e.key === "Enter") {
        const form = e.target.closest("form");
        if (form) {
          e.preventDefault();
          form.requestSubmit();
        }
      }
    };
    return /* @__PURE__ */ jsx9("div", { ref, className: cn("inline-flex gap-2", attached && "gap-0", className), children: Array.from({ length }, (_, i) => /* @__PURE__ */ jsx9(
      "input",
      {
        ref: (el) => {
          inputRefs.current[i] = el;
        },
        type: mask ? "password" : "text",
        inputMode: type === "numeric" ? "numeric" : "text",
        maxLength: 1,
        value: values[i],
        placeholder,
        disabled,
        onChange: (e) => handleInput(i, e.target.value),
        onKeyDown: (e) => handleKeyDown(i, e),
        className: cn(
          pinInputFieldVariants({ variant, size }),
          attached && i > 0 && "rounded-l-none -ml-px",
          attached && i < length - 1 && "rounded-r-none"
        )
      },
      i
    )) });
  }
);
PinInput.displayName = "PinInput";

// src/components/ui/badge.tsx
import * as React8 from "react";
import { cva as cva6 } from "class-variance-authority";
import { jsx as jsx10 } from "react/jsx-runtime";
var badgeVariants = cva6("inline-flex items-center font-medium rounded-full whitespace-nowrap", {
  variants: {
    variant: {
      solid: "bg-primary-500 text-white",
      subtle: "bg-primary-50 text-primary-700",
      outline: "border border-primary-500 text-primary-500 bg-transparent",
      surface: "bg-neutral-100 text-neutral-700",
      plain: "text-neutral-700"
    },
    size: {
      xs: "text-[10px] px-1 h-4",
      sm: "text-xs px-1.5 h-5",
      md: "text-xs px-2 h-6",
      lg: "text-sm px-2.5 h-7"
    }
  },
  defaultVariants: {
    variant: "subtle",
    size: "sm"
  }
});
var Badge = React8.forwardRef(({ className, variant, size, ...props }, ref) => {
  return /* @__PURE__ */ jsx10("span", { className: cn(badgeVariants({ variant, size, className })), ref, ...props });
});
Badge.displayName = "Badge";

// src/components/ui/card.tsx
import * as React9 from "react";
import { cva as cva7 } from "class-variance-authority";
import { jsx as jsx11 } from "react/jsx-runtime";
var cardVariants = cva7("flex flex-col relative min-w-0 break-words rounded-[12px] text-start", {
  variants: {
    variant: {
      outline: "bg-white border border-neutral-200",
      elevated: "bg-white shadow-md",
      subtle: "bg-neutral-100"
    },
    size: {
      sm: "[--card-padding:theme(spacing.4)]",
      md: "[--card-padding:theme(spacing.6)]",
      lg: "[--card-padding:theme(spacing.7)]"
    }
  },
  defaultVariants: {
    variant: "outline",
    size: "md"
  }
});
var Card = React9.forwardRef(({ className, variant, size, ...props }, ref) => /* @__PURE__ */ jsx11("div", { ref, className: cn(cardVariants({ variant, size }), className), ...props }));
Card.displayName = "Card";
var CardHeader = React9.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
    "div",
    {
      ref,
      className: cn("flex flex-col gap-1.5 px-[var(--card-padding)] pt-[var(--card-padding)]", className),
      ...props
    }
  )
);
CardHeader.displayName = "CardHeader";
var CardTitle = React9.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx11("div", { ref, className: cn("font-semibold", className), ...props })
);
CardTitle.displayName = "CardTitle";
var CardDescription = React9.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx11("div", { ref, className: cn("text-sm text-neutral-600", className), ...props })
);
CardDescription.displayName = "CardDescription";
var CardBody = React9.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx11("div", { ref, className: cn("p-[var(--card-padding)] flex-1 flex flex-col", className), ...props })
);
CardBody.displayName = "CardBody";
var CardFooter = React9.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
    "div",
    {
      ref,
      className: cn("flex items-center gap-2 px-[var(--card-padding)] pb-[var(--card-padding)]", className),
      ...props
    }
  )
);
CardFooter.displayName = "CardFooter";

// src/components/ui/table.tsx
import * as React10 from "react";
import { jsx as jsx12 } from "react/jsx-runtime";
var Table = React10.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx12("div", { className: "relative w-full overflow-auto", children: /* @__PURE__ */ jsx12("table", { ref, className: cn("w-full caption-bottom text-sm", className), ...props }) })
);
Table.displayName = "Table";
var TableHeader = React10.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx12("thead", { ref, className: cn("[&_tr]:border-b", className), ...props })
);
TableHeader.displayName = "TableHeader";
var TableBody = React10.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx12("tbody", { ref, className: cn("[&_tr:last-child]:border-0", className), ...props })
);
TableBody.displayName = "TableBody";
var TableFooter = React10.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
    "tfoot",
    {
      ref,
      className: cn("border-t bg-neutral-50 font-medium [&>tr]:last:border-b-0", className),
      ...props
    }
  )
);
TableFooter.displayName = "TableFooter";
var TableRow = React10.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
    "tr",
    {
      ref,
      className: cn(
        "border-b border-neutral-200 transition-colors hover:bg-neutral-50 data-[state=selected]:bg-neutral-100",
        className
      ),
      ...props
    }
  )
);
TableRow.displayName = "TableRow";
var TableHead = React10.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
    "th",
    {
      ref,
      className: cn(
        "h-10 px-4 text-left align-middle font-medium text-neutral-500 [&:has([role=checkbox])]:pr-0",
        className
      ),
      ...props
    }
  )
);
TableHead.displayName = "TableHead";
var TableCell = React10.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx12("td", { ref, className: cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className), ...props })
);
TableCell.displayName = "TableCell";
var TableCaption = React10.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx12("caption", { ref, className: cn("mt-4 text-sm text-neutral-500", className), ...props })
);
TableCaption.displayName = "TableCaption";

// src/components/ui/avatar.tsx
import * as React11 from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { jsx as jsx13 } from "react/jsx-runtime";
var Avatar = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx13(
  AvatarPrimitive.Root,
  {
    ref,
    className: cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className),
    ...props
  }
));
Avatar.displayName = "Avatar";
var AvatarImage = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx13(AvatarPrimitive.Image, { ref, className: cn("aspect-square h-full w-full", className), ...props }));
AvatarImage.displayName = "AvatarImage";
var AvatarFallback = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx13(
  AvatarPrimitive.Fallback,
  {
    ref,
    className: cn(
      "flex h-full w-full items-center justify-center rounded-full bg-neutral-100 text-neutral-600",
      className
    ),
    ...props
  }
));
AvatarFallback.displayName = "AvatarFallback";

// src/components/ui/alert.tsx
import * as React12 from "react";
import { cva as cva8 } from "class-variance-authority";
import { jsx as jsx14 } from "react/jsx-runtime";
var alertVariants = cva8("relative flex gap-3 rounded-lg p-4", {
  variants: {
    variant: {
      subtle: "",
      solid: "text-white",
      outline: "border bg-transparent"
    },
    status: {
      info: "",
      warning: "",
      error: "",
      success: ""
    }
  },
  compoundVariants: [
    // subtle
    { variant: "subtle", status: "info", className: "bg-primary-50 text-primary-700" },
    { variant: "subtle", status: "warning", className: "bg-warning-50 text-warning-700" },
    { variant: "subtle", status: "error", className: "bg-error-50 text-error-700" },
    { variant: "subtle", status: "success", className: "bg-success-50 text-success-700" },
    // solid
    { variant: "solid", status: "info", className: "bg-primary-500" },
    { variant: "solid", status: "warning", className: "bg-warning-500" },
    { variant: "solid", status: "error", className: "bg-error-500" },
    { variant: "solid", status: "success", className: "bg-success-500" },
    // outline
    { variant: "outline", status: "info", className: "border-primary-500 text-primary-700" },
    { variant: "outline", status: "warning", className: "border-warning-500 text-warning-700" },
    { variant: "outline", status: "error", className: "border-error-500 text-error-700" },
    { variant: "outline", status: "success", className: "border-success-500 text-success-700" }
  ],
  defaultVariants: {
    variant: "subtle",
    status: "info"
  }
});
var Alert = React12.forwardRef(({ className, variant, status, ...props }, ref) => {
  return /* @__PURE__ */ jsx14("div", { className: cn(alertVariants({ variant, status, className })), ref, role: "alert", ...props });
});
Alert.displayName = "Alert";
var AlertTitle = React12.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx14("h5", { className: cn("font-medium", className), ref, ...props });
  }
);
AlertTitle.displayName = "AlertTitle";
var AlertDescription = React12.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx14("p", { className: cn("text-paragraph-sm", className), ref, ...props });
  }
);
AlertDescription.displayName = "AlertDescription";
var AlertIndicator = React12.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx14("span", { className: cn("shrink-0", className), ref, ...props });
  }
);
AlertIndicator.displayName = "AlertIndicator";

// src/components/ui/skeleton.tsx
import * as React13 from "react";
import { cva as cva9 } from "class-variance-authority";
import { jsx as jsx15 } from "react/jsx-runtime";
var skeletonVariants = cva9(
  "rounded-lg cursor-default text-transparent pointer-events-none select-none shrink-0 [&::before]:invisible [&::after]:invisible [&>*]:invisible",
  {
    variants: {
      variant: {
        pulse: "bg-neutral-100 animate-pulse",
        shine: "bg-[length:400%_100%] bg-[linear-gradient(270deg,var(--skeleton-start,theme(colors.neutral.100)),var(--skeleton-end,theme(colors.neutral.50)),var(--skeleton-end,theme(colors.neutral.50)),var(--skeleton-start,theme(colors.neutral.100)))] animate-[bg-position_5s_ease-in-out_infinite]",
        none: "bg-neutral-100 animate-none"
      },
      loading: {
        true: "",
        false: "bg-transparent animate-[fade-in_0.1s_ease-out] [&::before]:visible [&::after]:visible [&>*]:visible"
      }
    },
    defaultVariants: {
      variant: "pulse",
      loading: true
    }
  }
);
var Skeleton = React13.forwardRef(({ className, variant, loading, ...props }, ref) => {
  return /* @__PURE__ */ jsx15("div", { className: cn(skeletonVariants({ variant, loading }), className), ref, ...props });
});
Skeleton.displayName = "Skeleton";

// src/components/ui/spinner.tsx
import * as React14 from "react";
import { cva as cva10 } from "class-variance-authority";
import { jsx as jsx16, jsxs as jsxs4 } from "react/jsx-runtime";
var spinnerVariants = cva10("animate-spin", {
  variants: {
    size: {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      md: "h-6 w-6",
      lg: "h-8 w-8",
      xl: "h-12 w-12"
    }
  },
  defaultVariants: {
    size: "md"
  }
});
var Spinner = React14.forwardRef(
  ({ className, size, color = "text-primary-500", ...props }, ref) => {
    return /* @__PURE__ */ jsxs4(
      "svg",
      {
        className: cn(spinnerVariants({ size }), color, className),
        ref,
        xmlns: "http://www.w3.org/2000/svg",
        fill: "none",
        viewBox: "0 0 24 24",
        ...props,
        children: [
          /* @__PURE__ */ jsx16("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "2", fill: "none" }),
          /* @__PURE__ */ jsx16(
            "circle",
            {
              className: "opacity-75",
              cx: "12",
              cy: "12",
              r: "10",
              stroke: "currentColor",
              strokeWidth: "2",
              fill: "none",
              strokeLinecap: "round",
              strokeDasharray: "62.83",
              strokeDashoffset: "47.12"
            }
          )
        ]
      }
    );
  }
);
Spinner.displayName = "Spinner";

// src/components/ui/progress.tsx
import * as React15 from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { jsx as jsx17 } from "react/jsx-runtime";
var Progress = React15.forwardRef(({ className, value, ...props }, ref) => /* @__PURE__ */ jsx17(
  ProgressPrimitive.Root,
  {
    ref,
    className: cn("relative h-2 w-full overflow-hidden rounded-full bg-neutral-200", className),
    ...props,
    children: /* @__PURE__ */ jsx17(
      ProgressPrimitive.Indicator,
      {
        className: "h-full w-full flex-1 bg-primary-500 transition-all",
        style: { transform: `translateX(-${100 - (value || 0)}%)` }
      }
    )
  }
));
Progress.displayName = "Progress";

// src/components/ui/dialog.tsx
import { createContext as createContext2, useCallback as useCallback2, useContext as useContext2 } from "react";
import { XIcon } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

// src/contexts/ShadowContainerContext.tsx
import { createContext, useContext } from "react";
var ShadowContainerContext = createContext({
  container: null,
  setOverlayActive: () => {
  }
});
var ShadowContainerProvider = ShadowContainerContext.Provider;
var useShadowContainer = () => {
  const { container } = useContext(ShadowContainerContext);
  return container;
};
var useShadowOverlay = () => {
  const { setOverlayActive } = useContext(ShadowContainerContext);
  return setOverlayActive;
};

// src/components/ui/dialog.tsx
import { jsx as jsx18, jsxs as jsxs5 } from "react/jsx-runtime";
var DialogContext = createContext2({});
function Dialog({
  onExitComplete,
  onOpenChange,
  ...props
}) {
  const setOverlayActive = useShadowOverlay();
  const close = useCallback2(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);
  const handleOpenChange = useCallback2(
    (open) => {
      setOverlayActive(open);
      onOpenChange?.(open);
    },
    [onOpenChange, setOverlayActive]
  );
  return /* @__PURE__ */ jsx18(DialogContext.Provider, { value: { onExitComplete, close }, children: /* @__PURE__ */ jsx18(DialogPrimitive.Root, { "data-slot": "dialog", onOpenChange: handleOpenChange, ...props }) });
}
function DialogTrigger({ ...props }) {
  return /* @__PURE__ */ jsx18(DialogPrimitive.Trigger, { "data-slot": "dialog-trigger", ...props });
}
function DialogPortal({ container, ...props }) {
  const shadowContainer = useShadowContainer();
  return /* @__PURE__ */ jsx18(DialogPrimitive.Portal, { "data-slot": "dialog-portal", container: container ?? shadowContainer ?? void 0, ...props });
}
function DialogClose({ ...props }) {
  return /* @__PURE__ */ jsx18(DialogPrimitive.Close, { "data-slot": "dialog-close", ...props });
}
function DialogOverlay({ className, ...props }) {
  const { close } = useContext2(DialogContext);
  return /* @__PURE__ */ jsx18(
    DialogPrimitive.Overlay,
    {
      "data-slot": "dialog-overlay",
      className: cn(
        "fixed inset-0 z-modal bg-black/20 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:[animation-duration:300ms] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:[animation-duration:200ms]",
        className
      ),
      onClick: close,
      ...props
    }
  );
}
function DialogContent({
  className,
  children,
  showCloseButton = true,
  closeOnOverlayClick = true,
  overlayClassName,
  ...props
}) {
  const { onExitComplete, close } = useContext2(DialogContext);
  const handleAnimationEnd = useCallback2(
    (e) => {
      if (e.currentTarget.dataset.state === "closed") {
        onExitComplete?.();
      }
    },
    [onExitComplete]
  );
  const handleBackdropClick = useCallback2(
    (e) => {
      if (e.target === e.currentTarget && closeOnOverlayClick) {
        close?.();
      }
    },
    [close, closeOnOverlayClick]
  );
  return /* @__PURE__ */ jsxs5(DialogPortal, { "data-slot": "dialog-portal", children: [
    /* @__PURE__ */ jsx18(DialogOverlay, { className: overlayClassName }),
    /* @__PURE__ */ jsx18(
      DialogPrimitive.Content,
      {
        "data-slot": "dialog-content",
        className: "fixed inset-0 z-modal flex items-center justify-center overflow-y-auto p-4 outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:[animation-duration:200ms] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:[animation-duration:150ms]",
        onAnimationEnd: handleAnimationEnd,
        onClick: handleBackdropClick,
        ...props,
        children: /* @__PURE__ */ jsxs5(
          "div",
          {
            "data-slot": "dialog-inner",
            className: cn("relative w-full max-w-lg grid gap-4 rounded-lg bg-background p-6 shadow-lg", className),
            onClick: (e) => e.stopPropagation(),
            children: [
              children,
              showCloseButton && /* @__PURE__ */ jsxs5(
                DialogPrimitive.Close,
                {
                  "data-slot": "dialog-close",
                  className: "absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                  children: [
                    /* @__PURE__ */ jsx18(XIcon, {}),
                    /* @__PURE__ */ jsx18("span", { className: "sr-only", children: "Close" })
                  ]
                }
              )
            ]
          }
        )
      }
    )
  ] });
}
function DialogHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx18(
    "div",
    {
      "data-slot": "dialog-header",
      className: cn("flex flex-col gap-2 text-center sm:text-left", className),
      ...props
    }
  );
}
function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs5(
    "div",
    {
      "data-slot": "dialog-footer",
      className: cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className),
      ...props,
      children: [
        children,
        showCloseButton && /* @__PURE__ */ jsx18(DialogPrimitive.Close, { asChild: true, children: /* @__PURE__ */ jsx18(Button, { variant: "outline", children: "Close" }) })
      ]
    }
  );
}
function DialogTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx18(
    DialogPrimitive.Title,
    {
      "data-slot": "dialog-title",
      className: cn("text-lg leading-none font-semibold", className),
      ...props
    }
  );
}
function DialogDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx18(
    DialogPrimitive.Description,
    {
      "data-slot": "dialog-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/drawer.tsx
import * as React16 from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { jsx as jsx19, jsxs as jsxs6 } from "react/jsx-runtime";
function Drawer({
  noBodyStyles,
  onOpenChange,
  ...props
}) {
  const shadowContainer = useShadowContainer();
  const setOverlayActive = useShadowOverlay();
  const handleOpenChange = React16.useCallback(
    (open) => {
      setOverlayActive(open);
      onOpenChange?.(open);
    },
    [onOpenChange, setOverlayActive]
  );
  return /* @__PURE__ */ jsx19(
    DrawerPrimitive.Root,
    {
      "data-slot": "drawer",
      noBodyStyles: noBodyStyles ?? !!shadowContainer,
      onOpenChange: handleOpenChange,
      ...props
    }
  );
}
function DrawerTrigger({ ...props }) {
  return /* @__PURE__ */ jsx19(DrawerPrimitive.Trigger, { "data-slot": "drawer-trigger", ...props });
}
function DrawerPortal({ container, ...props }) {
  const shadowContainer = useShadowContainer();
  return /* @__PURE__ */ jsx19(DrawerPrimitive.Portal, { "data-slot": "drawer-portal", container: container ?? shadowContainer ?? void 0, ...props });
}
function DrawerClose({ ...props }) {
  return /* @__PURE__ */ jsx19(DrawerPrimitive.Close, { "data-slot": "drawer-close", ...props });
}
function DrawerOverlay({ className, ...props }) {
  return /* @__PURE__ */ jsx19(
    DrawerPrimitive.Overlay,
    {
      "data-slot": "drawer-overlay",
      className: cn(
        "fixed inset-0 z-modal bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
}
function DrawerContent({ className, children, ...props }) {
  return /* @__PURE__ */ jsxs6(DrawerPortal, { "data-slot": "drawer-portal", children: [
    /* @__PURE__ */ jsx19(DrawerOverlay, {}),
    /* @__PURE__ */ jsx19(
      DrawerPrimitive.Content,
      {
        "data-slot": "drawer-content",
        className: cn(
          "group/drawer-content fixed z-modal flex h-auto flex-col bg-background",
          "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg",
          "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4",
          "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4",
          className
        ),
        ...props,
        children
      }
    )
  ] });
}
function DrawerHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx19(
    "div",
    {
      "data-slot": "drawer-header",
      className: cn(
        "flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left",
        className
      ),
      ...props
    }
  );
}
function DrawerFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx19("div", { "data-slot": "drawer-footer", className: cn("mt-auto flex flex-col gap-2 p-4", className), ...props });
}
function DrawerTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx19(
    DrawerPrimitive.Title,
    {
      "data-slot": "drawer-title",
      className: cn("font-semibold text-foreground", className),
      ...props
    }
  );
}
function DrawerDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx19(
    DrawerPrimitive.Description,
    {
      "data-slot": "drawer-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/popover.tsx
import * as React17 from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { jsx as jsx20 } from "react/jsx-runtime";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverAnchor = PopoverPrimitive.Anchor;
var PopoverContent = React17.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx20(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx20(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-xl border border-neutral-200 bg-white p-4 shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = "PopoverContent";

// src/components/ui/tooltip.tsx
import * as React18 from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { jsx as jsx21 } from "react/jsx-runtime";
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = React18.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx21(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx21(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 overflow-hidden rounded-md bg-neutral-900 px-3 py-1.5 text-xs text-white animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
TooltipContent.displayName = "TooltipContent";

// src/components/ui/menu.tsx
import * as React19 from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { jsx as jsx22 } from "react/jsx-runtime";
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuContent = React19.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx22(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx22(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-lg border border-neutral-200 bg-white p-1 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = "DropdownMenuContent";
var DropdownMenuItem = React19.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx22(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-md px-2 py-1.5 text-sm outline-none transition-colors focus:bg-neutral-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = "DropdownMenuItem";
var DropdownMenuLabel = React19.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx22(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className),
    ...props
  }
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";
var DropdownMenuSeparator = React19.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx22(DropdownMenuPrimitive.Separator, { ref, className: cn("-mx-1 my-1 h-px bg-neutral-200", className), ...props }));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

// src/components/ui/tabs.tsx
import * as React20 from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { jsx as jsx23 } from "react/jsx-runtime";
var Tabs = TabsPrimitive.Root;
var TabsList = React20.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx23(
  TabsPrimitive.List,
  {
    ref,
    className: cn("inline-flex items-center justify-center rounded-lg bg-neutral-100 p-1", className),
    ...props
  }
));
TabsList.displayName = "TabsList";
var TabsTrigger = React20.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx23(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:shadow-sm",
      className
    ),
    ...props
  }
));
TabsTrigger.displayName = "TabsTrigger";
var TabsContent = React20.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx23(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = "TabsContent";

// src/components/ui/breadcrumb.tsx
import * as React21 from "react";
import { jsx as jsx24 } from "react/jsx-runtime";
var Breadcrumb = React21.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx24("nav", { ref, "aria-label": "breadcrumb", ...props }));
Breadcrumb.displayName = "Breadcrumb";
var BreadcrumbList = React21.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx24(
    "ol",
    {
      ref,
      className: cn("flex flex-wrap items-center gap-1.5 text-sm text-neutral-500", className),
      ...props
    }
  )
);
BreadcrumbList.displayName = "BreadcrumbList";
var BreadcrumbItem = React21.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx24("li", { ref, className: cn("inline-flex items-center gap-1.5", className), ...props })
);
BreadcrumbItem.displayName = "BreadcrumbItem";
var BreadcrumbLink = React21.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx24("a", { ref, className: cn("transition-colors hover:text-neutral-900", className), ...props })
);
BreadcrumbLink.displayName = "BreadcrumbLink";
var BreadcrumbPage = React21.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx24(
    "span",
    {
      ref,
      role: "link",
      "aria-disabled": "true",
      "aria-current": "page",
      className: cn("font-normal text-neutral-900", className),
      ...props
    }
  )
);
BreadcrumbPage.displayName = "BreadcrumbPage";
var BreadcrumbSeparator = ({ children, className, ...props }) => /* @__PURE__ */ jsx24("li", { role: "presentation", "aria-hidden": "true", className: cn("[&>svg]:w-3.5 [&>svg]:h-3.5", className), ...props, children: children ?? "/" });
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";
var BreadcrumbEllipsis = ({ className, ...props }) => /* @__PURE__ */ jsx24(
  "span",
  {
    role: "presentation",
    "aria-hidden": "true",
    className: cn("flex h-9 w-9 items-center justify-center", className),
    ...props,
    children: "\u2026"
  }
);
BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis";

// src/components/ui/accordion.tsx
import { ChevronDownIcon as ChevronDownIcon2 } from "lucide-react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { jsx as jsx25, jsxs as jsxs7 } from "react/jsx-runtime";
function Accordion({ ...props }) {
  return /* @__PURE__ */ jsx25(AccordionPrimitive.Root, { "data-slot": "accordion", ...props });
}
function AccordionItem({ className, ...props }) {
  return /* @__PURE__ */ jsx25(
    AccordionPrimitive.Item,
    {
      "data-slot": "accordion-item",
      className: cn("border-b last:border-b-0", className),
      ...props
    }
  );
}
function AccordionTrigger({ className, children, ...props }) {
  return /* @__PURE__ */ jsx25(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs7(
    AccordionPrimitive.Trigger,
    {
      "data-slot": "accordion-trigger",
      className: cn(
        "flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx25(ChevronDownIcon2, { className: "pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200" })
      ]
    }
  ) });
}
function AccordionContent({ className, children, ...props }) {
  return /* @__PURE__ */ jsx25(
    AccordionPrimitive.Content,
    {
      "data-slot": "accordion-content",
      className: "overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      ...props,
      children: /* @__PURE__ */ jsx25("div", { className: cn("pt-0 pb-4", className), children })
    }
  );
}

// src/components/ui/link.tsx
import * as React22 from "react";
import { cva as cva11 } from "class-variance-authority";
import { jsx as jsx26 } from "react/jsx-runtime";
var LINK_DECORATION = {
  underline: "text-neutral-800 underline underline-offset-[3px] decoration-current/20",
  plain: "text-neutral-800 no-underline underline-offset-[3px] hover:underline hover:decoration-current/20"
};
var linkDecorationVariants = cva11("", {
  variants: {
    variant: LINK_DECORATION
  },
  defaultVariants: {
    variant: "plain"
  }
});
var LINK_BASE_STYLES = "inline-flex items-center outline-none gap-1.5 cursor-pointer rounded focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500";
var linkVariants = cva11(LINK_BASE_STYLES, {
  variants: {
    variant: LINK_DECORATION
  },
  defaultVariants: {
    variant: "plain"
  }
});
var Link = React22.forwardRef(
  ({ className, variant, asChild, unstyled, children, ...props }, ref) => {
    const computedClassName = cn(unstyled ? linkDecorationVariants({ variant }) : linkVariants({ variant }), className);
    if (asChild && React22.isValidElement(children)) {
      return React22.cloneElement(children, {
        ...props,
        ...children.props,
        className: cn(computedClassName, children.props.className),
        ref
      });
    }
    return /* @__PURE__ */ jsx26("a", { ref, className: computedClassName, ...props, children });
  }
);
Link.displayName = "Link";

// src/components/ui/separator.tsx
import * as React23 from "react";
import { jsx as jsx27 } from "react/jsx-runtime";
var Separator3 = React23.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => {
    return /* @__PURE__ */ jsx27(
      "div",
      {
        className: cn(
          "shrink-0 bg-neutral-200",
          orientation === "horizontal" ? "h-px w-full" : "w-px h-full",
          className
        ),
        ref,
        role: decorative ? "none" : "separator",
        "aria-orientation": decorative ? void 0 : orientation,
        "data-orientation": orientation,
        ...props
      }
    );
  }
);
Separator3.displayName = "Separator";

// src/components/snippet/toaster.tsx
import { Toaster as SonnerToaster, toast } from "sonner";

// src/assets/lucideIcons.ts
var lucideIcons_exports = {};
__export(lucideIcons_exports, {
  ArrowDownIcon: () => ArrowDownIcon,
  ArrowLeftIcon: () => ArrowLeftIcon,
  ArrowRightIcon: () => ArrowRightIcon,
  ArrowUpIcon: () => ArrowUpIcon,
  Box: () => Box,
  Check: () => Check,
  ChevronDown: () => ChevronDown,
  ChevronLeft: () => ChevronLeft,
  ChevronRight: () => ChevronRight,
  ChevronUp: () => ChevronUp,
  Circle: () => Circle,
  CircleAlert: () => CircleAlert,
  CircleCheck: () => CircleCheck,
  CircleMinus: () => CircleMinus,
  CirclePlus: () => CirclePlus,
  CircleUserRound: () => CircleUserRound,
  Clock: () => Clock,
  Copy: () => Copy,
  Download: () => Download,
  ExternalLink: () => ExternalLink,
  Eye: () => Eye,
  EyeOff: () => EyeOff,
  Globe: () => Globe,
  Heart: () => Heart,
  Image: () => Image2,
  Info: () => Info,
  MailOpen: () => MailOpen,
  MapPin: () => MapPin,
  Minus: () => Minus,
  MoveLeft: () => MoveLeft,
  MoveRight: () => MoveRight,
  Package: () => Package,
  PackageSearch: () => PackageSearch,
  PercentCircle: () => PercentCircle,
  Phone: () => Phone,
  Plane: () => Plane,
  Plus: () => Plus,
  RefreshCcwIcon: () => RefreshCcwIcon,
  RefreshCwIcon: () => RefreshCwIcon,
  Search: () => Search,
  ShieldAlert: () => ShieldAlert,
  ShieldCheck: () => ShieldCheck,
  SlidersHorizontal: () => SlidersHorizontal,
  Sparkles: () => Sparkles,
  Star: () => Star,
  ThumbsUp: () => ThumbsUp,
  TriangleAlert: () => TriangleAlert,
  Truck: () => Truck,
  User: () => User,
  Volume2: () => Volume2,
  VolumeX: () => VolumeX,
  X: () => X
});
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Check,
  Phone,
  PercentCircle,
  Sparkles,
  MailOpen,
  MapPin,
  CircleUserRound,
  Heart,
  Minus,
  Plus,
  RefreshCcwIcon,
  RefreshCwIcon,
  Search,
  Star,
  X,
  CircleCheck,
  CircleAlert,
  User,
  Image as Image2,
  ThumbsUp,
  MoveLeft,
  MoveRight,
  Info,
  CirclePlus,
  CircleMinus,
  ExternalLink,
  Volume2,
  VolumeX,
  SlidersHorizontal,
  Eye,
  EyeOff,
  ShieldCheck,
  Package,
  Globe,
  Plane,
  Box,
  Truck,
  Circle,
  Clock,
  Copy,
  PackageSearch,
  ShieldAlert,
  TriangleAlert,
  Download
} from "lucide-react";

// src/assets/icons.ts
var LUCIDE_ICONS = {
  ...lucideIcons_exports
};

// src/components/LucideIcon.tsx
import { forwardRef as forwardRef23 } from "react";
import { jsx as jsx28 } from "react/jsx-runtime";
var LucideIcon = forwardRef23(
  ({ name, size, boxSize, className, width, height, ...props }, ref) => {
    const LucideIconComponent = LUCIDE_ICONS[name];
    if (!LucideIconComponent) {
      console.error(`Icon "${name}" not found in Icons, please add icon in src/assets/icons.ts`);
      return null;
    }
    const resolvedSize = size ?? boxSize;
    const resolvedWidth = width ?? resolvedSize;
    const resolvedHeight = height ?? resolvedSize;
    return /* @__PURE__ */ jsx28(LucideIconComponent, { ref, className, width: resolvedWidth, height: resolvedHeight, ...props });
  }
);
LucideIcon.displayName = "LucideIcon";

// src/components/snippet/toaster.tsx
import { jsx as jsx29, jsxs as jsxs8 } from "react/jsx-runtime";
var MOBILE_NAV_HEIGHT = "63px";
var copyToast = (title) => {
  toast.custom(
    () => /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-center gap-1 rounded-[20px] bg-neutral-800 opacity-90 px-4 py-3 w-fit mx-auto", children: [
      /* @__PURE__ */ jsx29(LucideIcon, { name: "Check", className: "w-5 h-5 text-white" }),
      /* @__PURE__ */ jsx29("span", { className: "text-label-medium font-medium text-white text-center whitespace-nowrap", children: title })
    ] }),
    { duration: 3e3 }
  );
};
var FRAUD_TOAST_CLASSES = "flex items-center justify-center gap-1.5 rounded-full shadow-[0px_2px_8px_rgba(0,0,0,0.16)] h-9 px-3 w-fit mx-auto bg-white/20";
var fraudToast = {
  warning: ({ id, title, duration = 5e3 }) => {
    toast.custom(
      () => /* @__PURE__ */ jsxs8("div", { role: "alert", "aria-live": "polite", "aria-atomic": "true", className: FRAUD_TOAST_CLASSES, children: [
        /* @__PURE__ */ jsx29(LucideIcon, { name: "TriangleAlert", className: "w-4 h-4 text-warning-600", "aria-hidden": "true" }),
        /* @__PURE__ */ jsx29("span", { className: "text-label-large text-warning-600 whitespace-nowrap", children: title })
      ] }),
      { id, duration }
    );
  },
  penalty: ({ id, title, duration = 5e3 }) => {
    toast.custom(
      () => /* @__PURE__ */ jsxs8("div", { role: "alert", "aria-live": "assertive", "aria-atomic": "true", className: FRAUD_TOAST_CLASSES, children: [
        /* @__PURE__ */ jsx29(LucideIcon, { name: "ShieldAlert", className: "w-4 h-4 text-primary-600", "aria-hidden": "true" }),
        /* @__PURE__ */ jsx29("span", { className: "text-label-large text-primary-600 whitespace-nowrap", children: title })
      ] }),
      { id, duration }
    );
  }
};
var Toaster = () => {
  return /* @__PURE__ */ jsx29(
    SonnerToaster,
    {
      position: "bottom-center",
      expand: true,
      mobileOffset: { bottom: `calc(${MOBILE_NAV_HEIGHT} + env(safe-area-inset-bottom))` },
      toastOptions: { style: { width: "100%" } }
    }
  );
};

// src/components/snippet/carousel.tsx
import * as React27 from "react";
import useEmblaCarousel from "embla-carousel-react";

// ../utils/src/bridge/WpBridge.ts
var WP_EVENT_CHANNEL = "wp-event";
function isWindowAvailable() {
  return typeof window !== "undefined" && typeof window.addEventListener === "function";
}
function normalizePayload(detail) {
  const { type: _ignored, ...payload } = detail;
  return payload;
}
var WpBridge = class {
  debug;
  constructor(options) {
    this.debug = Boolean(options?.debug);
  }
  /**
   * Subscribe to a specific WP event type
   */
  on(type, handler) {
    if (!isWindowAvailable()) {
      return () => void 0;
    }
    const listener = (event) => {
      const customEvent = event;
      const detail = customEvent.detail;
      if (!detail || detail.type !== type) {
        return;
      }
      const payload = normalizePayload(detail);
      if (this.debug) {
        console.info("[WpBridge] event", type, payload);
      }
      handler(payload);
    };
    window.addEventListener(WP_EVENT_CHANNEL, listener);
    if (this.debug) {
      console.info("[WpBridge] on", type);
    }
    return () => {
      window.removeEventListener(WP_EVENT_CHANNEL, listener);
      if (this.debug) {
        console.info("[WpBridge] off", type);
      }
    };
  }
  /**
   * Subscribe once only
   */
  once(type, handler) {
    const unsubscribe = this.on(type, (payload) => {
      try {
        handler(payload);
      } finally {
        unsubscribe();
      }
    });
    return unsubscribe;
  }
  /**
   * Dispatch helper for tests/manual triggers
   */
  dispatch(type, payload) {
    if (!isWindowAvailable()) {
      return;
    }
    const detail = { type, ...payload };
    window.dispatchEvent(new CustomEvent(WP_EVENT_CHANNEL, { detail }));
  }
};
var wpBridge = new WpBridge();

// ../utils/src/function/env.ts
function isServer() {
  return typeof window === "undefined" && typeof global !== "undefined";
}

// ../utils/src/hooks/useAsyncEffect.ts
import { useEffect as useEffect2 } from "react";

// ../utils/src/hooks/useCombinedRef.ts
import { useCallback as useCallback4 } from "react";
function useCombinedRefs(...refs) {
  return useCallback4(
    (value) => {
      for (const ref of refs) {
        if (typeof ref === "function") {
          ref(value);
        } else if (ref != null) {
          ref.current = value;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refs
  );
}

// ../utils/src/hooks/useDidUpdate.ts
import { useEffect as useEffect3, useRef as useRef2 } from "react";

// ../utils/src/hooks/useEffectOnce.ts
import { useEffect as useEffect4, useRef as useRef3 } from "react";

// ../utils/src/hooks/useEventListener.ts
import { useEffect as useEffect5, useRef as useRef4 } from "react";

// ../utils/src/hooks/useImpression.ts
import { useCallback as useCallback5, useMemo } from "react";

// ../utils/src/hooks/useImpressionRef.ts
import { useCallback as useCallback8, useEffect as useEffect8, useMemo as useMemo2, useRef as useRef7 } from "react";

// ../utils/src/hooks/usePreservedCallback.ts
import { useCallback as useCallback6, useEffect as useEffect6, useRef as useRef5 } from "react";
function usePreservedCallback(callback) {
  const callbackRef = useRef5(callback);
  useEffect6(() => {
    callbackRef.current = callback;
  }, [callback]);
  return useCallback6((...args) => {
    return callbackRef.current(...args);
  }, []);
}

// ../utils/src/hooks/useVisibilityEvent.ts
import { useEffect as useEffect7 } from "react";
function useVisibilityEvent(callback) {
  const handleVisibilityChange = usePreservedCallback(() => {
    callback(document.visibilityState);
  });
  useEffect7(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange]);
}

// ../utils/src/hooks/useRefEvent.ts
import { useCallback as useCallback7, useRef as useRef6 } from "react";

// ../../node_modules/es-toolkit/dist/function/debounce.mjs
function debounce(func, debounceMs, { signal, edges } = {}) {
  let pendingThis = void 0;
  let pendingArgs = null;
  const leading = edges != null && edges.includes("leading");
  const trailing = edges == null || edges.includes("trailing");
  const invoke = () => {
    if (pendingArgs !== null) {
      func.apply(pendingThis, pendingArgs);
      pendingThis = void 0;
      pendingArgs = null;
    }
  };
  const onTimerEnd = () => {
    if (trailing) {
      invoke();
    }
    cancel();
  };
  let timeoutId = null;
  const schedule = () => {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      timeoutId = null;
      onTimerEnd();
    }, debounceMs);
  };
  const cancelTimer = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
  const cancel = () => {
    cancelTimer();
    pendingThis = void 0;
    pendingArgs = null;
  };
  const flush = () => {
    invoke();
  };
  const debounced = function(...args) {
    if (signal?.aborted) {
      return;
    }
    pendingThis = this;
    pendingArgs = args;
    const isFirstCall = timeoutId == null;
    schedule();
    if (leading && isFirstCall) {
      invoke();
    }
  };
  debounced.schedule = schedule;
  debounced.cancel = cancel;
  debounced.flush = flush;
  signal?.addEventListener("abort", cancel, { once: true });
  return debounced;
}

// ../../node_modules/es-toolkit/dist/function/noop.mjs
function noop() {
}

// ../utils/src/hooks/useRefEvent.ts
function useRefEffect(callback, deps) {
  const preservedCallback = usePreservedCallback(callback);
  const disposeRef = useRef6(noop);
  const effect = useCallback7(
    (element) => {
      disposeRef.current();
      disposeRef.current = noop;
      if (element != null) {
        const cleanup = callback(element);
        if (typeof cleanup === "function") {
          disposeRef.current = cleanup;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preservedCallback, ...deps]
  );
  return effect;
}

// ../utils/src/hooks/useImpressionRef.ts
function useImpressionRef({
  onImpressionStart: _onImpressionStart = noop,
  onImpressionEnd: _onImpressionEnd = noop,
  timeThreshold = 0,
  root,
  rootMargin,
  areaThreshold: intersectThreshold = 0,
  impressionOnce = false
}) {
  const onImpressionStart = usePreservedCallback(_onImpressionStart);
  const onImpressionEnd = usePreservedCallback(_onImpressionEnd);
  const isIntersectingRef = useRef7(false);
  const hasImpressionStartedRef = useRef7(false);
  const hasImpressionEndedRef = useRef7(false);
  const setDebouncedIsImpressed = useSetDebouncedBooleanValue({
    onValueChange: (isImpressed) => {
      if (isImpressed) {
        if (impressionOnce && hasImpressionStartedRef.current) {
          return;
        }
        hasImpressionStartedRef.current = true;
        onImpressionStart();
      } else {
        if (impressionOnce && hasImpressionEndedRef.current) {
          return;
        }
        hasImpressionEndedRef.current = true;
        onImpressionEnd();
      }
    },
    timeThreshold
  });
  const intersectionObserverRef = useIntersectionObserver(
    ({ isIntersecting }) => {
      if (document.visibilityState === "hidden") {
        return;
      }
      isIntersectingRef.current = isIntersecting;
      setDebouncedIsImpressed(isIntersecting);
    },
    {
      root,
      rootMargin,
      threshold: intersectThreshold
    }
  );
  useVisibilityEvent((visibilityState) => {
    if (!isIntersectingRef.current) {
      return;
    }
    setDebouncedIsImpressed(visibilityState === "visible");
  });
  return intersectionObserverRef;
}
function useIntersectionObserver(_callback, options) {
  const callback = usePreservedCallback(_callback);
  const observer = useMemo2(() => {
    if (typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer2 = new IntersectionObserver(([entry]) => {
      if (!entry) {
        return;
      }
      callback(entry);
    }, options);
    return observer2;
  }, [callback, options]);
  const ref = useRefEffect(
    (element) => {
      observer?.observe(element);
      return () => {
        observer?.unobserve(element);
      };
    },
    [callback, options]
  );
  return ref;
}
function useSetDebouncedBooleanValue(options) {
  const { onValueChange, timeThreshold } = options;
  const handleValueChange = usePreservedCallback(onValueChange);
  const ref = useRef7({ value: false, cancelPrevDebounce: noop });
  const setDebouncedValue = useCallback8(
    (newValue) => {
      if (newValue === ref.current.value) {
        return;
      }
      const debounced = debounce(() => {
        handleValueChange(newValue);
        ref.current.value = newValue;
      }, timeThreshold ?? 100);
      ref.current.cancelPrevDebounce();
      debounced();
      ref.current.cancelPrevDebounce = debounced.cancel;
    },
    [handleValueChange, timeThreshold]
  );
  useEffect8(() => {
    const current = ref.current;
    return () => {
      current.cancelPrevDebounce();
    };
  }, []);
  return setDebouncedValue;
}

// ../utils/src/hooks/useIsClient.ts
import { useSyncExternalStore } from "react";
var emptySubscribe = () => noop;
var getSnapshot = () => true;
var getServerSnapshot = () => false;
var useIsClient = () => useSyncExternalStore(emptySubscribe, getSnapshot, getServerSnapshot);

// ../utils/src/hooks/usePageInfo.ts
import { useEffect as useEffect9, useState as useState2 } from "react";

// ../utils/src/hooks/useOutsideClickEffect.ts
import { useEffect as useEffect10, useRef as useRef8 } from "react";

// ../utils/src/hooks/useCallbackRef.ts
import { useRef as useRef9, useEffect as useEffect11, useMemo as useMemo3 } from "react";

// ../utils/src/hooks/useIsTouchDevice.ts
import { useEffect as useEffect12, useState as useState3 } from "react";

// ../utils/src/hooks/useMutationLoading.ts
import { useState as useState4, useEffect as useEffect13 } from "react";

// ../utils/src/hooks/useDebouncedNumberCommit.ts
import { useEffect as useEffect14, useMemo as useMemo4, useState as useState5 } from "react";

// ../utils/src/hooks/useStep.ts
import { useState as useState6, useCallback as useCallback9, useMemo as useMemo5 } from "react";

// ../utils/src/hooks/useCountdown.ts
import { useState as useState7, useEffect as useEffect15, useCallback as useCallback10 } from "react";

// ../utils/src/hooks/useElementPosition.ts
import { useCallback as useCallback11, useEffect as useEffect16, useRef as useRef10, useState as useState8 } from "react";

// ../utils/src/logger/Logger.tsx
import { createContext as createContext3, useState as useState9, useEffect as useEffect17, useContext as useContext3, useCallback as useCallback12, useMemo as useMemo6 } from "react";
import { jsx as jsx30 } from "react/jsx-runtime";
var defaultAnalyticsContext = {
  isInitialized: false,
  debug: false,
  identify: () => {
  },
  group: () => {
  },
  trackEvent: () => {
  },
  metadata: {},
  appName: ""
};
var LoggerContext = createContext3(defaultAnalyticsContext);

// ../utils/src/logger/hooks/useLogger.ts
import { useCallback as useCallback13, useMemo as useMemo7 } from "react";

// ../utils/src/logger/hooks/usePageLogger.tsx
import { createContext as createContext4, useContext as useContext4 } from "react";
import { jsx as jsx31 } from "react/jsx-runtime";
var PageLoggerContext = createContext4(void 0);

// ../utils/src/logger/components/LoggingEvent.tsx
import { cloneElement as cloneElement2 } from "react";

// ../utils/src/logger/components/LoggingClick.tsx
import { jsx as jsx32 } from "react/jsx-runtime";

// ../utils/src/react/ImpressionArea.tsx
import { forwardRef as forwardRef25 } from "react";

// ../utils/src/react/Slot/Slot.tsx
import React25 from "react";

// ../utils/src/react/Slot/@common/Sloattable/Slottable.tsx
import React24 from "react";
import { Fragment, jsx as jsx33 } from "react/jsx-runtime";
function Slottable({ children }) {
  return /* @__PURE__ */ jsx33(Fragment, { children });
}
function isSlottable(child) {
  return React24.isValidElement(child) && child.type === Slottable;
}

// ../../node_modules/react-simplikit/esm/index.js
import { forwardRef as forwardRef24 } from "react";
import { useRef as useRef42 } from "react";
import { useCallback as useCallback22, useEffect as useEffect22, useMemo as useMemo8, useRef as useRef22 } from "react";
import { useCallback as useCallback14, useEffect as useEffect18, useRef as useRef11 } from "react";
import { useMemo as useMemo22 } from "react";
import { useCallback as useCallback32, useRef as useRef32 } from "react";
import { useCallback as useCallback42, useEffect as useEffect32 } from "react";
import { jsx as jsx34 } from "react/jsx-runtime";
import { Children, Fragment as Fragment2, isValidElement as isValidElement2 } from "react";
import { Fragment as Fragment22, jsx as jsx210, jsxs as jsxs9 } from "react/jsx-runtime";
import { useEffect as useEffect42 } from "react";
import { useCallback as useCallback52, useState as useState10 } from "react";
import { useEffect as useEffect52, useRef as useRef52 } from "react";
import { useCallback as useCallback62, useEffect as useEffect62, useRef as useRef62 } from "react";
import { useCallback as useCallback72, useState as useState22 } from "react";
import { useCallback as useCallback82, useState as useState32 } from "react";
import { useEffect as useEffect72 } from "react";
import { useMemo as useMemo32 } from "react";
import { useCallback as useCallback92, useEffect as useEffect82, useRef as useRef72 } from "react";
import { useCallback as useCallback102, useEffect as useEffect92, useRef as useRef82, useState as useState42 } from "react";
import { useCallback as useCallback112, useState as useState52 } from "react";
import { useEffect as useEffect102 } from "react";
import { useEffect as useEffect112, useLayoutEffect } from "react";
import { useCallback as useCallback122, useEffect as useEffect122, useMemo as useMemo42, useRef as useRef92, useState as useState62 } from "react";
import { useCallback as useCallback132, useRef as useRef102 } from "react";
import { useCallback as useCallback142, useMemo as useMemo62, useState as useState72 } from "react";
import { useMemo as useMemo52, useRef as useRef112 } from "react";
import { useEffect as useEffect132, useRef as useRef12 } from "react";
import { useRef as useRef13 } from "react";
import { useCallback as useCallback15, useRef as useRef14, useSyncExternalStore as useSyncExternalStore2 } from "react";
import { useEffect as useEffect142, useMemo as useMemo72 } from "react";
import { useEffect as useEffect152 } from "react";
import { useReducer } from "react";
import { createContext as createContext5, useContext as useContext5, useMemo as useMemo82 } from "react";
import { jsx as jsx35 } from "react/jsx-runtime";
function debounce2(func, debounceMs, { edges = ["leading", "trailing"] } = {}) {
  let pendingThis = void 0;
  let pendingArgs = null;
  const leading = edges != null && edges.includes("leading");
  const trailing = edges == null || edges.includes("trailing");
  const invoke = () => {
    if (pendingArgs !== null) {
      func.apply(pendingThis, pendingArgs);
      pendingThis = void 0;
      pendingArgs = null;
    }
  };
  const onTimerEnd = () => {
    if (trailing) {
      invoke();
    }
    cancel();
  };
  let timeoutId = null;
  const schedule = () => {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      timeoutId = null;
      onTimerEnd();
    }, debounceMs);
  };
  const cancelTimer = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
  const cancel = () => {
    cancelTimer();
    pendingThis = void 0;
    pendingArgs = null;
  };
  const debounced = function(...args) {
    pendingThis = this;
    pendingArgs = args;
    const isFirstCall = timeoutId == null;
    schedule();
    if (leading && isFirstCall) {
      invoke();
    }
  };
  debounced.cancel = cancel;
  return debounced;
}
function usePreservedCallback2(callback) {
  const callbackRef = useRef11(callback);
  useEffect18(() => {
    callbackRef.current = callback;
  }, [callback]);
  return useCallback14((...args) => {
    return callbackRef.current(...args);
  }, []);
}
function useDebouncedCallback({
  onChange,
  timeThreshold,
  leading = false,
  trailing = true
}) {
  const handleChange = usePreservedCallback2(onChange);
  const ref = useRef22({ value: false, clearPreviousDebounce: () => {
  } });
  useEffect22(() => {
    const current = ref.current;
    return () => {
      current.clearPreviousDebounce();
    };
  }, []);
  const edges = useMemo8(() => {
    const _edges = [];
    if (leading) {
      _edges.push("leading");
    }
    if (trailing) {
      _edges.push("trailing");
    }
    return _edges;
  }, [leading, trailing]);
  return useCallback22(
    (nextValue) => {
      if (nextValue === ref.current.value) {
        return;
      }
      const debounced = debounce2(
        () => {
          handleChange(nextValue);
          ref.current.value = nextValue;
        },
        timeThreshold,
        { edges }
      );
      ref.current.clearPreviousDebounce();
      debounced();
      ref.current.clearPreviousDebounce = debounced.cancel;
    },
    [handleChange, timeThreshold, edges]
  );
}
function useRefEffect2(callback, deps) {
  const preservedCallback = usePreservedCallback2(callback);
  const cleanupCallbackRef = useRef32(() => {
  });
  const effect = useCallback32(
    (element) => {
      cleanupCallbackRef.current();
      cleanupCallbackRef.current = () => {
      };
      if (element == null) {
        return;
      }
      const cleanup = preservedCallback(element);
      if (typeof cleanup === "function") {
        cleanupCallbackRef.current = cleanup;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preservedCallback, ...deps]
  );
  return effect;
}
function useIntersectionObserver2(callback, options) {
  const preservedCallback = usePreservedCallback2(callback);
  const observer = useMemo22(() => {
    if (typeof IntersectionObserver === "undefined") {
      return;
    }
    return new IntersectionObserver(([entry]) => {
      preservedCallback(entry);
    }, options);
  }, [preservedCallback, options]);
  return useRefEffect2(
    (element) => {
      observer?.observe(element);
      return () => {
        observer?.unobserve(element);
      };
    },
    [preservedCallback, options]
  );
}
function useVisibilityEvent2(callback, options = {}) {
  const handleVisibilityChange = useCallback42(() => {
    callback(document.visibilityState);
  }, [callback]);
  useEffect32(() => {
    if (options?.immediate ?? false) {
      handleVisibilityChange();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange, options?.immediate]);
}
function useImpressionRef2({
  onImpressionStart = () => {
  },
  onImpressionEnd = () => {
  },
  rootMargin,
  areaThreshold = 0,
  timeThreshold = 0
}) {
  const impressionStartHandler = usePreservedCallback2(onImpressionStart);
  const impressionEndHandler = usePreservedCallback2(onImpressionEnd);
  const isIntersectingRef = useRef42(false);
  const impressionEventHandler = useDebouncedCallback({
    timeThreshold,
    onChange: (impressed) => {
      (impressed ? impressionStartHandler : impressionEndHandler)();
    },
    leading: true
  });
  useVisibilityEvent2((documentVisible) => {
    if (!isIntersectingRef.current) {
      return;
    }
    impressionEventHandler(documentVisible === "visible");
  });
  return useIntersectionObserver2(
    (entry) => {
      if (document.visibilityState === "hidden") {
        return;
      }
      const currentRatio = entry.intersectionRatio;
      const isIntersecting = areaThreshold === 0 ? entry.isIntersecting : currentRatio >= areaThreshold;
      isIntersectingRef.current = isIntersecting;
      impressionEventHandler(isIntersecting);
    },
    { rootMargin, threshold: areaThreshold }
  );
}
function mergeRefs(...refs) {
  return (value) => {
    for (const ref of refs) {
      if (ref == null) {
        continue;
      }
      if (typeof ref === "function") {
        ref(value);
        continue;
      }
      ref.current = value;
    }
  };
}
var ImpressionArea = forwardRef24(ImpressionAreaImpl);
function ImpressionAreaImpl({ as, rootMargin, areaThreshold, timeThreshold, onImpressionStart, onImpressionEnd, ...props }, ref) {
  const Component2 = as ?? "div";
  const impressionRef = useImpressionRef2({
    onImpressionStart,
    onImpressionEnd,
    areaThreshold,
    timeThreshold,
    rootMargin
  });
  return /* @__PURE__ */ jsx34(Component2, { ref: mergeRefs(ref, impressionRef), ...props });
}
Object.assign(ImpressionArea, {
  displayName: "ImpressionArea"
});
var MemoStorage = class {
  storage = /* @__PURE__ */ new Map();
  get(key) {
    return this.storage.get(key) ?? null;
  }
  set(key, value) {
    this.storage.set(key, value);
  }
  remove(key) {
    this.storage.delete(key);
  }
  clear() {
    this.storage.clear();
  }
};
var LocalStorage = class {
  static canUse() {
    const TEST_KEY = generateTestKey();
    try {
      localStorage.setItem(TEST_KEY, "test");
      localStorage.removeItem(TEST_KEY);
      return true;
    } catch {
      return false;
    }
  }
  get(key) {
    return localStorage.getItem(key);
  }
  set(key, value) {
    localStorage.setItem(key, value);
  }
  remove(key) {
    localStorage.removeItem(key);
  }
  clear() {
    localStorage.clear();
  }
};
var SessionStorage = class {
  static canUse() {
    const TEST_KEY = generateTestKey();
    try {
      sessionStorage.setItem(TEST_KEY, "test");
      sessionStorage.removeItem(TEST_KEY);
      return true;
    } catch {
      return false;
    }
  }
  get(key) {
    return sessionStorage.getItem(key);
  }
  set(key, value) {
    sessionStorage.setItem(key, value);
  }
  remove(key) {
    sessionStorage.removeItem(key);
  }
  clear() {
    sessionStorage.clear();
  }
};
function generateTestKey() {
  return new Array(4).fill(null).map(() => Math.random().toString(36).slice(2)).join("");
}
function generateStorage() {
  if (LocalStorage.canUse()) {
    return new LocalStorage();
  }
  return new MemoStorage();
}
function generateSessionStorage() {
  if (SessionStorage.canUse()) {
    return new SessionStorage();
  }
  return new MemoStorage();
}
var safeLocalStorage = generateStorage();
var safeSessionStorage = generateSessionStorage();
function mergeProps(...props) {
  return props.reduce(pushProp, {});
}
function pushProp(prev, curr) {
  for (const key in curr) {
    if (curr[key] === void 0) continue;
    switch (key) {
      case "className": {
        prev[key] = [prev[key], curr[key]].join(" ").trim();
        break;
      }
      case "style": {
        prev[key] = mergeStyle(prev[key], curr[key]);
        break;
      }
      default: {
        const mergedFunction = mergeFunction(prev[key], curr[key]);
        if (mergedFunction) {
          prev[key] = mergedFunction;
        } else if (curr[key] !== void 0) {
          prev[key] = curr[key];
        }
      }
    }
  }
  return prev;
}
function mergeStyle(a, b) {
  if (a == null) return b;
  return { ...a, ...b };
}
function mergeFunction(a, b) {
  if (typeof a === "function" && typeof b === "function") {
    return (...args) => {
      a(...args);
      b(...args);
    };
  }
}

// ../utils/src/react/Slot/Slot.tsx
import { jsx as jsx36 } from "react/jsx-runtime";
var Slot2 = React25.forwardRef(({ children, ...slotProps }, ref) => {
  const childrenList = React25.Children.toArray(children);
  const slottable = childrenList.find(isSlottable);
  if (slottable != null) {
    const newElement = slottable.props.children;
    const newChildren = childrenList.map((child) => {
      if (child !== slottable) {
        return child;
      }
      if (React25.Children.count(newElement) > 1) {
        return React25.Children.only(null);
      }
      return React25.isValidElement(newElement) ? newElement.props.children : null;
    });
    return /* @__PURE__ */ jsx36(SlotClone, { ...slotProps, ref, children: React25.isValidElement(newElement) ? React25.cloneElement(newElement, void 0, newChildren) : null });
  }
  return /* @__PURE__ */ jsx36(SlotClone, { ...slotProps, ref, children });
});
var SlotClone = React25.forwardRef((props, forwardedRef) => {
  const { children, ...slotProps } = props;
  if (React25.isValidElement(children)) {
    const mergedProps = mergeProps(slotProps, children.props);
    const childRef = children.ref;
    const mergedRef = forwardedRef ? mergeRefs(forwardedRef, childRef) : childRef;
    return React25.cloneElement(
      children,
      {
        ...mergedProps,
        ref: mergedRef
      }
    );
  }
  return null;
});

// ../utils/src/react/ImpressionArea.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var ImpressionArea2 = forwardRef25(
  ({
    root,
    rootMargin,
    areaThreshold,
    timeThreshold,
    impressionOnce,
    className,
    children,
    onImpressionStart,
    onImpressionEnd,
    style,
    ...otherProps
  }, forwardedRef) => {
    const impressionRef = useImpressionRef({
      onImpressionStart,
      onImpressionEnd,
      areaThreshold,
      timeThreshold,
      impressionOnce,
      root,
      rootMargin
    });
    const ref = useCombinedRefs(forwardedRef, impressionRef);
    return /* @__PURE__ */ jsx37(Slot2, { className, ref, style, ...otherProps, children });
  }
);
ImpressionArea2.displayName = "ImpressionArea";

// ../utils/src/logger/components/LoggingImpression.tsx
import { jsx as jsx38 } from "react/jsx-runtime";

// ../utils/src/logger/components/LoggingView.tsx
import { useEffect as useEffect19, useState as useState11, useRef as useRef15 } from "react";
import { Fragment as Fragment3, jsx as jsx39 } from "react/jsx-runtime";

// ../utils/src/react/TextHighlighter.tsx
import React26, { useMemo as useMemo9 } from "react";
import { Fragment as Fragment4, jsx as jsx40 } from "react/jsx-runtime";

// ../utils/src/react/ErrorBoundary.tsx
import { cloneElement as cloneElement3, Component } from "react";

// ../utils/src/react/createSafeContext.ts
import { createContext as createContext6, useContext as useContext6 } from "react";
var NullSymbol = Symbol("Null");
function createSafeContext(displayName) {
  const Context = createContext6(NullSymbol);
  Context.displayName = displayName ?? "SafeContext";
  function useSafeContext() {
    const context = useContext6(Context);
    if (context === NullSymbol) {
      const error2 = new Error(`[${Context.displayName}]: Provider not found.`);
      error2.name = "[Error] Context";
      throw error2;
    }
    return context;
  }
  return [Context.Provider, useSafeContext];
}

// ../utils/src/react/ClientOnly.tsx
import { Fragment as Fragment5, jsx as jsx41 } from "react/jsx-runtime";
var ClientOnly = Object.assign(
  ({ children, fallback }) => /* @__PURE__ */ jsx41(Fragment5, { children: useIsClient() ? children : fallback }),
  {
    displayName: "ClientOnly",
    with: (clientOnlyProps, Component2) => Object.assign(
      (props) => /* @__PURE__ */ jsx41(ClientOnly, { ...clientOnlyProps, children: /* @__PURE__ */ jsx41(Component2, { ...props }) }),
      { displayName: `${ClientOnly.displayName}.with(${Component2.displayName || Component2.name || "Component"})` }
    )
  }
);

// ../utils/src/react/withSuspense.tsx
import { Suspense } from "react";
import { jsx as jsx42 } from "react/jsx-runtime";

// ../utils/src/storage/makeExternalStore.ts
import { useSyncExternalStore as useSyncExternalStore3 } from "react";

// ../utils/src/storage/localStorageEngine.ts
var clientLocalStorageEngine = {
  getItem: (key) => localStorage.getItem(key),
  setItem: (key, value) => localStorage.setItem(key, value),
  removeItem: (key) => localStorage.removeItem(key)
};
var serverStorageEngine = {
  getItem: () => null,
  setItem: noop,
  removeItem: noop
};
var localStorageEngine = isServer() ? serverStorageEngine : clientLocalStorageEngine;

// ../utils/src/personalization/core/constants.ts
var EVENT_TYPE = {
  VIEW: "View",
  CLICK: "Click",
  IMPRESSION: "Impression",
  ADD_TO_CART: "AddToCart",
  REMOVE_FROM_CART: "RemoveFromCart",
  PURCHASE: "Purchase",
  SIGNUP: "Signup",
  REFERRAL: "Referral",
  REWARD_REDEMPTION: "RewardRedemption",
  REVIEW_WRITE: "ReviewWrite",
  HOME_PAGE_VIEW: "HomePageView",
  BROWSING: "Browsing"
};
var SESSION_TIMEOUT_MS = 30 * 60 * 1e3;
var HIGH_VALUE_EVENT_TYPES = /* @__PURE__ */ new Set([EVENT_TYPE.PURCHASE]);

// ../utils/src/personalization/core/adapters/cookieStorageAdapter.ts
var DEFAULT_ACTOR_MAX_AGE = 365 * 24 * 60 * 60;

// ../utils/src/personalization/react/PersonalizationProvider.tsx
import { useEffect as useEffect20, useRef as useRef16 } from "react";
import { jsx as jsx43 } from "react/jsx-runtime";
var [PersonalizationContextProvider, usePersonalizationContext] = createSafeContext("PersonalizationContext");

// ../utils/src/personalization/react/components/TrackView.tsx
import { useEffect as useEffect21, useRef as useRef17 } from "react";
import { Fragment as Fragment6, jsx as jsx44 } from "react/jsx-runtime";

// ../utils/src/personalization/react/components/TrackImpression.tsx
import { jsx as jsx45 } from "react/jsx-runtime";

// ../utils/src/personalization/react/components/TrackEvent.tsx
import { cloneElement as cloneElement4 } from "react";

// ../utils/src/personalization/react/components/TrackClick.tsx
import { cloneElement as cloneElement5 } from "react";

// src/components/snippet/carousel.tsx
import { Fragment as Fragment7, jsx as jsx46, jsxs as jsxs10 } from "react/jsx-runtime";
var [CarouselProvider, useCarousel] = createSafeContext("CarouselContext");
var CarouselContent = React27.forwardRef(
  ({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();
    return /* @__PURE__ */ jsx46("div", { ref: carouselRef, className: "overflow-hidden", children: /* @__PURE__ */ jsx46("div", { ref, className: cn("flex", orientation === "vertical" && "flex-col", className), ...props }) });
  }
);
CarouselContent.displayName = "CarouselContent";
var CarouselItem = React27.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx46(
      "div",
      {
        ref,
        role: "group",
        "aria-roledescription": "slide",
        className: cn("min-w-0 shrink-0 grow-0 basis-full", className),
        ...props
      }
    );
  }
);
CarouselItem.displayName = "CarouselItem";
var CarouselDotButton = React27.forwardRef(
  ({ className, index, children, render, ...props }, ref) => {
    const { selectedIndex, onDotButtonClick } = useCarousel();
    const isSelected = index === selectedIndex;
    const onClick = React27.useCallback(() => {
      onDotButtonClick(index);
    }, [onDotButtonClick, index]);
    if (render) {
      return /* @__PURE__ */ jsx46(Fragment7, { children: render({ onClick, isSelected, index }) });
    }
    return /* @__PURE__ */ jsx46(
      "button",
      {
        ref,
        type: "button",
        onClick,
        "aria-current": isSelected,
        "data-selected": isSelected,
        className: cn("h-6 w-6 p-0", className),
        ...props,
        children
      }
    );
  }
);
CarouselDotButton.displayName = "CarouselDotButton";
var CarouselDots = React27.forwardRef(({ renderDot, className, ...props }, ref) => {
  const { scrollSnaps } = useCarousel();
  return /* @__PURE__ */ jsx46("div", { ref, className: cn("flex", className), ...props, children: scrollSnaps.map((_, index) => /* @__PURE__ */ jsx46(
    CarouselDotButton,
    {
      index,
      render: renderDot ? (buttonProps) => renderDot(buttonProps) : void 0
    },
    index
  )) });
});
CarouselDots.displayName = "CarouselDots";
var CarouselPrevious = React27.forwardRef(
  ({ className, children, ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();
    return /* @__PURE__ */ jsx46(
      "button",
      {
        ref,
        type: "button",
        className: cn(
          "absolute h-8 w-8 rounded-full border border-neutral-200 bg-white flex items-center justify-center",
          orientation === "horizontal" ? "-left-12 top-1/2 -translate-y-1/2" : "left-1/2 -top-12 -translate-x-1/2 rotate-90",
          className
        ),
        disabled: !canScrollPrev,
        onClick: scrollPrev,
        ...props,
        children: children || /* @__PURE__ */ jsxs10(Fragment7, { children: [
          /* @__PURE__ */ jsx46(LucideIcon, { name: "ChevronLeft", size: 16 }),
          /* @__PURE__ */ jsx46("span", { className: "sr-only", children: "Previous" })
        ] })
      }
    );
  }
);
CarouselPrevious.displayName = "CarouselPrevious";
var CarouselNext = React27.forwardRef(
  ({ className, children, ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();
    return /* @__PURE__ */ jsx46(
      "button",
      {
        ref,
        type: "button",
        className: cn(
          "absolute h-8 w-8 rounded-full border border-neutral-200 bg-white flex items-center justify-center",
          orientation === "horizontal" ? "-right-12 top-1/2 -translate-y-1/2" : "left-1/2 -bottom-12 -translate-x-1/2 rotate-90",
          className
        ),
        disabled: !canScrollNext,
        onClick: scrollNext,
        ...props,
        children: children || /* @__PURE__ */ jsxs10(Fragment7, { children: [
          /* @__PURE__ */ jsx46(LucideIcon, { name: "ChevronRight", size: 16 }),
          /* @__PURE__ */ jsx46("span", { className: "sr-only", children: "Next" })
        ] })
      }
    );
  }
);
CarouselNext.displayName = "CarouselNext";
var CarouselComponent = React27.forwardRef(
  ({ orientation = "horizontal", opts, setApi, plugins, className, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y"
      },
      plugins
    );
    const [canScrollPrev, setCanScrollPrev] = React27.useState(false);
    const [canScrollNext, setCanScrollNext] = React27.useState(false);
    const [selectedIndex, setSelectedIndex] = React27.useState(0);
    const [scrollSnaps, setScrollSnaps] = React27.useState([]);
    const onDotButtonClick = React27.useCallback(
      (index) => {
        if (!api) {
          return;
        }
        api.scrollTo(index);
      },
      [api]
    );
    const onSelect = React27.useCallback((api2) => {
      if (!api2) {
        return;
      }
      setSelectedIndex(api2.selectedScrollSnap());
      setScrollSnaps(api2.scrollSnapList());
      setCanScrollPrev(api2.canScrollPrev());
      setCanScrollNext(api2.canScrollNext());
    }, []);
    const scrollPrev = React27.useCallback(() => {
      api?.scrollPrev();
    }, [api]);
    const scrollNext = React27.useCallback(() => {
      api?.scrollNext();
    }, [api]);
    const handleKeyDown = React27.useCallback(
      (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );
    React27.useEffect(() => {
      if (!api || !setApi) {
        return;
      }
      setApi(api);
    }, [api, setApi]);
    React27.useEffect(() => {
      if (!api) {
        return;
      }
      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);
      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);
    return /* @__PURE__ */ jsx46(
      CarouselProvider,
      {
        value: {
          carouselRef,
          api,
          opts,
          orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
          selectedIndex,
          scrollSnaps,
          onDotButtonClick
        },
        children: /* @__PURE__ */ jsx46(
          "div",
          {
            ref,
            onKeyDownCapture: handleKeyDown,
            className: cn("relative", className),
            role: "region",
            "aria-roledescription": "carousel",
            ...props,
            children
          }
        )
      }
    );
  }
);
CarouselComponent.displayName = "Carousel";
var Carousel = Object.assign(CarouselComponent, {
  Content: CarouselContent,
  Item: CarouselItem,
  Previous: CarouselPrevious,
  Next: CarouselNext,
  DotButton: CarouselDotButton,
  Dots: CarouselDots
});

// src/components/ui/command.tsx
import * as React28 from "react";
import { Command as CommandPrimitive } from "cmdk";
import { jsx as jsx47 } from "react/jsx-runtime";
var Command = React28.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx47(
  CommandPrimitive,
  {
    ref,
    className: cn("flex h-full w-full flex-col overflow-hidden rounded-xl bg-white text-neutral-900", className),
    ...props
  }
));
Command.displayName = "Command";
var CommandInput = React28.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx47(
  CommandPrimitive.Input,
  {
    ref,
    className: cn(
      "flex h-10 w-full rounded-md bg-transparent px-3 py-2 text-sm outline-none placeholder:text-neutral-400 disabled:cursor-not-allowed disabled:opacity-50",
      className
    ),
    ...props
  }
));
CommandInput.displayName = "CommandInput";
var CommandList = React28.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx47(
  CommandPrimitive.List,
  {
    ref,
    className: cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className),
    ...props
  }
));
CommandList.displayName = "CommandList";
var CommandEmpty = React28.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx47(CommandPrimitive.Empty, { ref, className: cn("py-6 text-center text-sm text-neutral-500", className), ...props }));
CommandEmpty.displayName = "CommandEmpty";
var CommandGroup = React28.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx47(
  CommandPrimitive.Group,
  {
    ref,
    className: cn(
      "overflow-hidden [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-neutral-500",
      className
    ),
    ...props
  }
));
CommandGroup.displayName = "CommandGroup";
var CommandSeparator = React28.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx47(CommandPrimitive.Separator, { ref, className: cn("-mx-1 h-px bg-neutral-200", className), ...props }));
CommandSeparator.displayName = "CommandSeparator";
var CommandItem = React28.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx47(
  CommandPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-pointer select-none items-center px-2 py-1.5 text-sm outline-none data-[selected=true]:bg-neutral-100/40 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
      className
    ),
    ...props
  }
));
CommandItem.displayName = "CommandItem";

// src/hooks/useMediaQuery.ts
import { useState as useState13, useEffect as useEffect24 } from "react";
var breakpoints = {
  mobile: "(min-width: 480px)",
  tablet: "(min-width: 768px)",
  desktop: "(min-width: 1160px)"
};
function useMediaQuery(breakpoint) {
  const [matches, setMatches] = useState13(false);
  useEffect24(() => {
    const query = breakpoints[breakpoint];
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [breakpoint]);
  return matches;
}
function useBreakpointValue(values) {
  const isDesktop = useMediaQuery("desktop");
  const isTablet = useMediaQuery("tablet");
  const isMobile = useMediaQuery("mobile");
  if (isDesktop && values.desktop !== void 0) return values.desktop;
  if (isTablet && values.tablet !== void 0) return values.tablet;
  if (isMobile && values.mobile !== void 0) return values.mobile;
  return values.base;
}

// src/components/ui/close-button.tsx
import * as React29 from "react";
import { cva as cva12 } from "class-variance-authority";
import { jsx as jsx48 } from "react/jsx-runtime";
var closeButtonVariants = cva12(
  "inline-flex items-center justify-center rounded-full bg-transparent border-none cursor-pointer text-current transition-colors focus-visible:outline-none",
  {
    variants: {
      size: {
        sm: "h-6 w-6 [&_svg]:size-3",
        md: "h-8 w-8 [&_svg]:size-4",
        lg: "h-10 w-10 [&_svg]:size-5"
      }
    },
    defaultVariants: {
      size: "sm"
    }
  }
);
var CloseButton = React29.forwardRef(
  ({ className, size, ...props }, ref) => /* @__PURE__ */ jsx48(
    "button",
    {
      ref,
      className: cn(closeButtonVariants({ size, className })),
      "aria-label": "Close",
      ...props,
      children: /* @__PURE__ */ jsx48("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", children: /* @__PURE__ */ jsx48("path", { d: "M18 6L6 18M6 6l12 12" }) })
    }
  )
);
CloseButton.displayName = "CloseButton";

// src/core/theme/tokens/colors.ts
var colors = {
  primary: {
    50: { value: "#F7F3FF" },
    75: { value: "#ECE3FF" },
    100: { value: "#E1D3FF" },
    200: { value: "#C7ADFF" },
    300: { value: "#976DF3" },
    400: { value: "#6F3FD7" },
    500: { value: "#582DB5" },
    600: { value: "#3D1690" },
    700: { value: "#311275" },
    800: { value: "#250A5D" },
    900: { value: "#200D48" }
  },
  neutral: {
    50: { value: "#F7F7F9" },
    75: { value: "#E8E8EB" },
    100: { value: "#D9D9DC" },
    200: { value: "#BBBABE" },
    300: { value: "#9D9BA1" },
    400: { value: "#7E7C83" },
    500: { value: "#605D66" },
    600: { value: "#413E48" },
    700: { value: "#231F2A" },
    800: { value: "#14101B" },
    900: { value: "#04000C" }
  },
  success: {
    50: { value: "#F0FDF4" },
    75: { value: "#E6FDEE" },
    100: { value: "#DCFCE7" },
    200: { value: "#BBF7D0" },
    300: { value: "#86EFAC" },
    400: { value: "#05DF73" },
    500: { value: "#00C950" },
    600: { value: "#0DA63E" },
    700: { value: "#15803D" },
    800: { value: "#016730" },
    900: { value: "#14532D" }
  },
  warning: {
    50: { value: "#FFFBEB" },
    75: { value: "#FFF7D9" },
    100: { value: "#FEF3C7" },
    200: { value: "#FDE68A" },
    300: { value: "#FCD34D" },
    400: { value: "#FBBF24" },
    500: { value: "#F59E0B" },
    600: { value: "#D97706" },
    700: { value: "#B45309" },
    800: { value: "#92400E" },
    900: { value: "#78350F" }
  },
  error: {
    50: { value: "#FEF2F2" },
    75: { value: "#FEEAEA" },
    100: { value: "#FEE2E2" },
    200: { value: "#FECACA" },
    300: { value: "#FFA2A3" },
    400: { value: "#FF6467" },
    500: { value: "#FA2C37" },
    600: { value: "#E7000B" },
    700: { value: "#C10008" },
    800: { value: "#9F0812" },
    900: { value: "#82181A" }
  },
  generic: {
    0: { value: "#FFFFFF" },
    100: { value: "#000000" }
  }
};

// src/core/theme/tokens/typographies.ts
var FONT_FAMILY = "var(--general-sans)";
var typographies = {
  // Display Styles (Desktop only)
  "display.large": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "52px",
      lineHeight: "64px",
      letterSpacing: "0em"
    }
  },
  "display.small": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "44px",
      lineHeight: "52px",
      letterSpacing: "0em"
    }
  },
  // Heading Styles - Desktop
  "heading.h1": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "40px",
      lineHeight: "52px",
      letterSpacing: "0em"
    }
  },
  "heading.h2": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "36px",
      lineHeight: "48px",
      letterSpacing: "0em"
    }
  },
  "heading.h3": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "32px",
      lineHeight: "44px",
      letterSpacing: "0em"
    }
  },
  "heading.h4": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "28px",
      lineHeight: "36px",
      letterSpacing: "0em"
    }
  },
  "heading.h5": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "24px",
      lineHeight: "32px",
      letterSpacing: "0em"
    }
  },
  "heading.h6": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "20px",
      lineHeight: "28px",
      letterSpacing: "0em"
    }
  },
  // Heading Styles - Mobile
  "heading.h1.mobile": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "36px",
      lineHeight: "48px",
      letterSpacing: "0em"
    }
  },
  "heading.h2.mobile": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "32px",
      lineHeight: "40px",
      letterSpacing: "0em"
    }
  },
  "heading.h3.mobile": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "28px",
      lineHeight: "36px",
      letterSpacing: "0em"
    }
  },
  "heading.h4.mobile": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "24px",
      lineHeight: "32px",
      letterSpacing: "0em"
    }
  },
  "heading.h5.mobile": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "20px",
      lineHeight: "28px",
      letterSpacing: "0em"
    }
  },
  "heading.h6.mobile": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "18px",
      lineHeight: "24px",
      letterSpacing: "0em"
    }
  },
  // Paragraph Styles
  "paragraph.large": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "18px",
      lineHeight: "28px",
      letterSpacing: "0em"
    }
  },
  "paragraph.medium": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: "0em"
    }
  },
  "paragraph.small": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0em"
    }
  },
  "paragraph.xsmall": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "12px",
      lineHeight: "20px",
      letterSpacing: "0em"
    }
  },
  // Overline (use with uppercase, fontWeight="medium")
  "paragraph.overline.large": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "1px"
    }
  },
  "paragraph.overline.small": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "12px",
      lineHeight: "20px",
      letterSpacing: "1px"
    }
  },
  // Label Styles
  "label.large": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "16px",
      lineHeight: "20px",
      letterSpacing: "0em"
    }
  },
  "label.medium": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0em"
    }
  },
  "label.small": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "12px",
      lineHeight: "16px",
      letterSpacing: "0em"
    }
  },
  "label.xsmall": {
    value: {
      fontFamily: FONT_FAMILY,
      fontSize: "10px",
      lineHeight: "16px",
      letterSpacing: "0em"
    }
  }
};

// src/core/theme/tokens/spacing.ts
var spacing = {
  "1": {
    value: "4px"
  },
  "2": {
    value: "8px"
  },
  "3": {
    value: "12px"
  },
  "4": {
    value: "16px"
  },
  "5": {
    value: "20px"
  },
  "6": {
    value: "24px"
  },
  "7": {
    value: "28px"
  },
  "8": {
    value: "32px"
  },
  "9": {
    value: "36px"
  },
  "10": {
    value: "40px"
  },
  "11": {
    value: "44px"
  },
  "12": {
    value: "48px"
  },
  "14": {
    value: "56px"
  },
  "16": {
    value: "64px"
  },
  "18": {
    value: "72px"
  },
  "20": {
    value: "80px"
  },
  "24": {
    value: "96px"
  },
  "28": {
    value: "112px"
  },
  "32": {
    value: "128px"
  },
  "36": {
    value: "144px"
  },
  "40": {
    value: "160px"
  },
  "44": {
    value: "176px"
  },
  "48": {
    value: "192px"
  },
  "52": {
    value: "208px"
  },
  "56": {
    value: "224px"
  },
  "60": {
    value: "240px"
  },
  "64": {
    value: "256px"
  },
  "72": {
    value: "288px"
  },
  "80": {
    value: "320px"
  },
  "96": {
    value: "384px"
  },
  "0.5": {
    value: "2px"
  },
  "1.5": {
    value: "6px"
  },
  "2.5": {
    value: "10px"
  },
  "3.5": {
    value: "14px"
  },
  "4.5": {
    value: "18px"
  },
  "safe-area-bottom": {
    value: "env(safe-area-inset-bottom)"
  },
  "safe-area-top": {
    value: "env(safe-area-inset-top)"
  },
  "safe-area-left": {
    value: "env(safe-area-inset-left)"
  },
  "safe-area-right": {
    value: "env(safe-area-inset-right)"
  }
};

// src/core/theme/tokens/font-sizes.ts
var fontSizes = {
  "2xs": {
    value: "10px"
  },
  xs: {
    value: "12px"
  },
  sm: {
    value: "14px"
  },
  md: {
    value: "16px"
  },
  lg: {
    value: "18px"
  },
  xl: {
    value: "20px"
  },
  "2xl": {
    value: "24px"
  },
  "3xl": {
    value: "28px"
  },
  "4xl": {
    value: "32px"
  },
  "5xl": {
    value: "36px"
  },
  "6xl": {
    value: "40px"
  },
  "7xl": {
    value: "48px"
  },
  "8xl": {
    value: "56px"
  },
  "9xl": {
    value: "64px"
  }
};

// src/core/theme/tokens/font-weights.ts
var fontWeights = {
  regular: {
    value: "400"
  },
  medium: {
    value: "500"
  },
  semibold: {
    value: "600"
  },
  bold: {
    value: "700"
  }
};

// src/core/theme/breakpoints.ts
var breakpoints2 = {
  mobile: "480px",
  tablet: "768px",
  desktop: "1160px"
};

// src/mobile/utils.ts
function convertColorTokenToFlat(colorToken) {
  const result = {};
  Object.entries(colorToken).forEach(([key, token]) => {
    result[key] = token.value;
  });
  return result;
}
function convertTypographyToken(typographyToken) {
  const { fontFamily, fontSize, lineHeight, letterSpacing, fontWeight } = typographyToken.value;
  return {
    fontFamily: fontFamily || "System",
    fontSize,
    lineHeight,
    letterSpacing,
    fontWeight
  };
}
function convertSpacingTokenToFlat(spacingToken) {
  const result = {};
  Object.entries(spacingToken).forEach(([key, token]) => {
    result[key] = token.value;
  });
  return result;
}
function convertFontSizeTokenToFlat(fontSizeToken) {
  const result = {};
  Object.entries(fontSizeToken).forEach(([key, token]) => {
    result[key] = token.value;
  });
  return result;
}
function convertFontWeightTokenToFlat(fontWeightToken) {
  const result = {};
  Object.entries(fontWeightToken).forEach(([key, token]) => {
    result[key] = token.value;
  });
  return result;
}
function createTailwindColors(colors2) {
  return {
    primary: convertColorTokenToFlat(colors2.primary),
    neutral: convertColorTokenToFlat(colors2.neutral),
    success: convertColorTokenToFlat(colors2.success),
    warning: convertColorTokenToFlat(colors2.warning),
    error: convertColorTokenToFlat(colors2.error),
    generic: convertColorTokenToFlat(colors2.generic)
  };
}
function createTailwindSpacing(spacing2) {
  return convertSpacingTokenToFlat(spacing2);
}
function createTailwindFontSizes(fontSizes2) {
  return convertFontSizeTokenToFlat(fontSizes2);
}
function createTailwindFontWeights(fontWeights2) {
  return convertFontWeightTokenToFlat(fontWeights2);
}
function getTypographyStyles(typography) {
  return {
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSize,
    lineHeight: typography.lineHeight,
    letterSpacing: typography.letterSpacing || "0"
  };
}
function convertBreakpointsToMobile(breakpoints3) {
  return {
    mobile: parseInt(breakpoints3.mobile, 10),
    tablet: parseInt(breakpoints3.tablet, 10),
    desktop: parseInt(breakpoints3.desktop, 10)
  };
}
function createTailwindBreakpoints(breakpoints3) {
  return {
    mobile: breakpoints3.mobile,
    tablet: breakpoints3.tablet,
    desktop: breakpoints3.desktop
  };
}
function createTailwindTextStyles(typographies2, customFontFamily) {
  const textStyles = {};
  const lineHeightOverrides = {
    "display.large": "65px",
    "display.small": "55px",
    "heading.h2": "45px",
    "heading.h1.mobile": "45px",
    "label.large": "20px",
    "label.medium": "18px",
    "paragraph.small": "18px"
  };
  Object.entries(typographies2).forEach(([key, token]) => {
    const utilityName = `text-style-${key.replace(/\./g, "-")}`;
    const { fontFamily, fontSize, lineHeight, letterSpacing } = token.value;
    const finalLineHeight = lineHeightOverrides[key] || lineHeight;
    textStyles[`.${utilityName}`] = {
      fontFamily: customFontFamily || fontFamily || "System",
      fontSize,
      lineHeight: finalLineHeight,
      letterSpacing: letterSpacing || "0"
    };
  });
  return textStyles;
}
function flattenTokens(tokens) {
  const out = {};
  for (const group of Object.keys(tokens)) {
    const shades = tokens[group];
    for (const shade of Object.keys(shades)) {
      out[`${group}.${shade}`] = shades[shade].value;
    }
  }
  return out;
}
var colorMapper = flattenTokens(colors);

// src/mobile/tokens.ts
var mobileColors = {
  primary: convertColorTokenToFlat(colors.primary),
  neutral: convertColorTokenToFlat(colors.neutral),
  success: convertColorTokenToFlat(colors.success),
  warning: convertColorTokenToFlat(colors.warning),
  error: convertColorTokenToFlat(colors.error),
  generic: convertColorTokenToFlat(colors.generic)
};
var mobileTypography = {
  display: {
    large: convertTypographyToken(typographies["display.large"]),
    small: convertTypographyToken(typographies["display.small"])
  },
  heading: {
    h1: convertTypographyToken(typographies["heading.h1"]),
    h2: convertTypographyToken(typographies["heading.h2"]),
    h3: convertTypographyToken(typographies["heading.h3"]),
    h4: convertTypographyToken(typographies["heading.h4"]),
    h5: convertTypographyToken(typographies["heading.h5"]),
    h6: convertTypographyToken(typographies["heading.h6"]),
    "h1.mobile": convertTypographyToken(typographies["heading.h1.mobile"]),
    "h2.mobile": convertTypographyToken(typographies["heading.h2.mobile"]),
    "h3.mobile": convertTypographyToken(typographies["heading.h3.mobile"]),
    "h4.mobile": convertTypographyToken(typographies["heading.h4.mobile"]),
    "h5.mobile": convertTypographyToken(typographies["heading.h5.mobile"]),
    "h6.mobile": convertTypographyToken(typographies["heading.h6.mobile"])
  },
  paragraph: {
    large: convertTypographyToken(typographies["paragraph.large"]),
    medium: convertTypographyToken(typographies["paragraph.medium"]),
    small: convertTypographyToken(typographies["paragraph.small"]),
    xsmall: convertTypographyToken(typographies["paragraph.xsmall"]),
    "overline.large": convertTypographyToken(typographies["paragraph.overline.large"]),
    "overline.small": convertTypographyToken(typographies["paragraph.overline.small"])
  },
  label: {
    large: convertTypographyToken(typographies["label.large"]),
    medium: convertTypographyToken(typographies["label.medium"]),
    small: convertTypographyToken(typographies["label.small"]),
    xsmall: convertTypographyToken(typographies["label.xsmall"])
  }
};
var mobileSpacing = convertSpacingTokenToFlat(spacing);
var mobileFontSizes = convertFontSizeTokenToFlat(fontSizes);
var mobileFontWeights = convertFontWeightTokenToFlat(fontWeights);
var mobileBreakpoints = convertBreakpointsToMobile(breakpoints2);
var mobileDesignTokens = {
  colors: mobileColors,
  typography: mobileTypography,
  spacing: mobileSpacing,
  fontSizes: mobileFontSizes,
  fontWeights: mobileFontWeights,
  breakpoints: mobileBreakpoints
};
var tailwindColors = createTailwindColors(colors);
var tailwindSpacing = createTailwindSpacing(spacing);
var tailwindFontSizes = createTailwindFontSizes(fontSizes);
var tailwindFontWeights = createTailwindFontWeights(fontWeights);
var tailwindBreakpoints = createTailwindBreakpoints(breakpoints2);
var tailwindTextStyles = createTailwindTextStyles(
  typographies,
  "General Sans, system-ui, -apple-system, sans-serif"
);
var tailwindConfig = {
  colors: tailwindColors,
  spacing: tailwindSpacing,
  fontSize: tailwindFontSizes,
  fontWeight: tailwindFontWeights,
  screens: tailwindBreakpoints,
  textStyles: tailwindTextStyles
};
var { primary, neutral, success, warning, error, generic } = mobileColors;
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertIndicator,
  AlertTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Carousel,
  Checkbox,
  CloseButton,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  LUCIDE_ICONS,
  Link,
  LucideIcon,
  PinInput,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Progress,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator3 as Separator,
  ShadowContainerProvider,
  Skeleton,
  Slider,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Toaster,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  alertVariants,
  badgeVariants,
  buttonVariants,
  cardVariants,
  cn,
  colorMapper,
  convertBreakpointsToMobile,
  convertColorTokenToFlat,
  convertFontSizeTokenToFlat,
  convertFontWeightTokenToFlat,
  convertSpacingTokenToFlat,
  convertTypographyToken,
  copyToast,
  createTailwindBreakpoints,
  createTailwindColors,
  createTailwindFontSizes,
  createTailwindFontWeights,
  createTailwindSpacing,
  createTailwindTextStyles,
  error,
  fraudToast,
  generic,
  getTypographyStyles,
  inputVariants,
  linkDecorationVariants,
  linkVariants,
  mobileBreakpoints,
  mobileColors,
  mobileDesignTokens,
  mobileFontSizes,
  mobileFontWeights,
  mobileSpacing,
  mobileTypography,
  neutral,
  pinInputFieldVariants,
  primary,
  spinnerVariants,
  success,
  tailwindBreakpoints,
  tailwindColors,
  tailwindConfig,
  tailwindFontSizes,
  tailwindFontWeights,
  tailwindSpacing,
  tailwindTextStyles,
  textareaVariants,
  toast,
  useBreakpointValue,
  useCarousel,
  useMediaQuery,
  useShadowContainer,
  useShadowOverlay,
  warning
};
